import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  onSnapshot,
  getFirestore,
  doc as fbDoc,
  getDoc,
  runTransaction
} from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { COLLECTION_PATHS, CUSTOMER_STATUS } from '../utils/constants';
import { turkishMultiSearch, toTurkishUpperCase } from '../utils/searchUtils';
import './CariKasa.css';
import { 
  User, 
  CreditCard,
  Search,
  Calendar,
  Filter,
  Plus,
  Edit2,
  Trash2,
  X,
  Download,
  FileText
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';
import { parseFlexible, toIsoUtcString, formatTrDateTime, formatTrDateOnly } from '../utils/hopsDate';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';
import 'react-datepicker/dist/react-datepicker.css';
import EditCariTransactionModal from './EditCariTransactionModal';

registerLocale('tr', tr);

/** Cari raporları: işlem satırları her zaman KDV hariç. KDV’li raporda yalnızca TOPLAM altında özet. */
const CARI_REPORT_KDV_RATE = 0.2;
const kdvTutariFromNetBakiye = (netBakiye) => (Number(netBakiye) || 0) * CARI_REPORT_KDV_RATE;
const genelToplamKdvDahil = (netBakiye) => (Number(netBakiye) || 0) * (1 + CARI_REPORT_KDV_RATE);

const CariKasa = () => {
  const [selectedCariList, setSelectedCariList] = useState([]); // Çoklu seçim için array
  const [cariList, setCariList] = useState([]);
  const [cariTransactions, setCariTransactions] = useState({}); // Her cari için ayrı transactions
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [bankalar, setBankalar] = useState([]);
  const [customerDebts, setCustomerDebts] = useState({}); // Müşteri borçlarını tutacak
  
  // Sıralama durumları
  const [order, setOrder] = useState('desc'); // 'asc' veya 'desc'
  const [orderBy, setOrderBy] = useState('Son İşlem'); // Varsayılan sıralama sütunu
  
  // Filtre state'leri
  const [selectedLastTransactionDays, setSelectedLastTransactionDays] = useState(null);
  const [selectedBalanceType, setSelectedBalanceType] = useState(null);
  const [cariFilters, setCariFilters] = useState({});
  
  // Son işlem tarihi filtre seçenekleri
  const lastTransactionDaysOptions = [
    { value: 0, label: 'Bugün' },
    { value: 2, label: 'Son 2 gün' },
    { value: 3, label: 'Son 3 gün' },
    { value: 4, label: 'Son 4 gün' },
    { value: 5, label: 'Son 5 gün' },
    { value: 6, label: 'Son 6 gün' },
    { value: 7, label: 'Son 7 gün' },
    { value: 14, label: 'Son 14 gün' },
    { value: 30, label: 'Son 30 gün' }
  ];

  // Bakiye türü filtre seçenekleri
  const balanceTypeOptions = [
    { value: 'alacakli', label: 'Alacaklı (- Bakiye)' },
    { value: 'borclu', label: 'Borçlu (+ Bakiye)' }
  ];
  
  // Her cari için ayrı filtre state'leri (zaten yukarıda tanımlanmış)
  
  // İşlem tablosu sıralama state'leri - Özel sıralamamız çalışsın diye null olarak başlatıyoruz
  const [transactionOrder, setTransactionOrder] = useState('asc');
  const [transactionOrderBy, setTransactionOrderBy] = useState(null);
  
  // Dinamik header yüksekliği için ref
  const tableHeaderRef = React.useRef(null);

  // Modal state'leri
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [deletedTransactionInfo, setDeletedTransactionInfo] = useState(null);
  const [kdvReportModal, setKdvReportModal] = useState({ open: false, type: null, cari: null });
  
  // Borç/Alacak ekleme başarı popup'ı için state'ler
  const [showAddSuccessPopup, setShowAddSuccessPopup] = useState(false);
  const [addedTransactionInfo, setAddedTransactionInfo] = useState(null);
  const [newTransaction, setNewTransaction] = useState({
    customerName: '',
    cariCode: '',
    transactionType: 'Borç', // 'Borç' veya 'Alacak'
    amount: '',
    date: toIsoUtcString(new Date()).slice(0, 10),
    description: '', // Açıklama alanı
    selectedCariId: null // Hangi cariye ait olduğunu belirtir
  });
  
  const [newCredit, setNewCredit] = useState({
    customerName: '',
    cariCode: '',
    paymentDescription: '', // Kullanıcı seçecek
    amount: '',
    date: toIsoUtcString(new Date()).slice(0, 10),
    description: '', // Açıklama alanı
    selectedCariId: null // Hangi cariye ait olduğunu belirtir
  });
  
  // Düzenleme modalı için state'ler
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Ödeme şekli seçenekleri - sabit seçenekler + banka havale seçenekleri
  const paymentMethodOptions = useMemo(() => {
    const fixedOptions = [
      { value: 'Nakit', label: 'Nakit' },
      { value: 'Çek', label: 'Çek' },
      { value: 'Kredi Kartı', label: 'Kredi Kartı' },
      { value: 'Devir Bakiyesi', label: 'Devir Bakiyesi' }
    ];

    const bankOptions = bankalar.map(banka => ({
      value: `${banka.bankaAdi} Havale`,
      label: `${banka.bankaAdi} Havale`
    }));

    return [...fixedOptions, ...bankOptions];
  }, [bankalar]);
  
  // Component mount olduğunda debug log
  console.log('🏦 CariKasa component render oldu');
  console.log('🏦 auth.currentUser:', auth.currentUser);
  console.log('🏦 cariList length:', cariList.length);

  // Summary artık customerDebts'ten hesaplanıyor

  // Cari listesini getir - MusteriListesi.js mantığını kullanıyoruz
  useEffect(() => {
    const db = getFirestore();
    let unsubscribeCustomers = null;

    // Mevcut kullanıcıyı kontrol et
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('CariKasa: Kullanıcı oturumu bulunamadı');
      setLoading(false);
      return;
    }

    console.log('CariKasa: Cari listesi alınıyor, userEmail:', currentUser.email);

    const customerListRef = collection(db, COLLECTION_PATHS.USER_CUSTOMERS(currentUser.email));
    
    unsubscribeCustomers = onSnapshot(
      customerListRef,
      {
        includeMetadataChanges: false // Metadata değişikliklerini dinleme - performans artışı
      },
      (snapshot) => {
        try {
          console.log('CariKasa: Snapshot alındı, docs count:', snapshot.docs.length);
          
          const customerData = snapshot.docs.map((doc) => {
            const data = doc.data();
            console.log(`🔍 Firebase Doc ${doc.id}:`, {
              'Müşteri Adı': data['Müşteri Adı'],
              'Onay': data['Onay'],
              'cariCode': data['cariCode'],
              'Telefon': data['Telefon'],
              'E-posta': data['E-posta'],
              'Şantiye Adı': data['Şantiye Adı'],
              'Şantiye': data['Şantiye']
            });
            return {
              id: doc.id,
              ...data,
              'Müşteri Adı': data['Müşteri Adı'] || '',
              'Onay': data['Onay'] || '',
              cariCode: data['cariCode'] || '',
              Şantiye: data['Şantiye'] || false,
              parentId: data.parentId || null,
            };
          });

          console.log('CariKasa: Tüm cari verisi:', customerData);

          const approvedData = customerData.filter(customer => {
            const onayDurumu = customer['Onay'];
            // Onaylanmış ve onay bekleyen müşterileri göster
            return onayDurumu === CUSTOMER_STATUS.APPROVED || 
                   onayDurumu === CUSTOMER_STATUS.PENDING || 
                   onayDurumu === CUSTOMER_STATUS.WAITING;
          });

          console.log('CariKasa: Onaylanmış ve onay bekleyen cari sayısı:', approvedData.length);
          setCariList(approvedData);
          
          // Loading'i hemen false yap - UI anında gösterilsin
          setLoading(false);
          
          // Borç bilgilerini arka planda yükle
          fetchAllCustomerDebts(currentUser.email, approvedData);
        } catch (error) {
          console.error('CariKasa: Veri işlenirken hata:', error);
          setLoading(false);
        }
      },
      (error) => {
        console.error('CariKasa: Firestore error:', error);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeCustomers) unsubscribeCustomers();
    };
  }, []);

  // === DEVRİMSEL YENİLİK: Dinamik Header Yüksekliği Ölçümü ===
  useEffect(() => {
    if (tableHeaderRef.current) {
      const headerHeight = tableHeaderRef.current.offsetHeight;
      // CSS değişkenini root'a ekle
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      console.log(`DYNAMIC HEADER HEIGHT: ${headerHeight}px olarak ayarlandı.`);
    }
  }, [cariList]); // Liste değiştiğinde yeniden hesapla

  // Tüm müşterilerin borç bilgilerini hızlı şekilde getir - BATCH işlemi
  const fetchAllCustomerDebts = async (userEmail, customers) => {
    console.log('🚀 HIZLI borç bilgileri yükleniyor...');
    console.log('📋 Müşteri listesi:', customers.map(c => ({ id: c.id, name: c['Müşteri Adı'] || c.customerName })));
    const startTime = Date.now();
    
    const db = getFirestore();
    const debts = {};
    
    // İlk olarak tüm müşteriler için 0 bakiye set et (anında gösterim)
    customers.forEach(customer => {
      // Debug: Vahap Çeliker kontrolü
      if (customer['Müşteri Adı'] && customer['Müşteri Adı'].toLowerCase().includes('vahap')) {
        console.log('🔍 Vahap Çeliker bulundu:', customer);
      }
      
      debts[customer.id] = {
        amount: 0,
        history: [],
        lastUpdated: null,
        loading: true // Yükleniyor durumu
      };
    });
    
    // Anında UI'yi güncelle
    setCustomerDebts(debts);
    
    // Arka planda batch işlemi ile gerçek verileri yükle
    const batchSize = 10; // 10'ar 10'ar işle
    for (let i = 0; i < customers.length; i += batchSize) {
      const batch = customers.slice(i, i + batchSize);
      
      // Paralel olarak bu batch'teki tüm müşterilerin verilerini al
      const batchPromises = batch.map(async (customer) => {
        try {
          const customerName = customer['Müşteri Adı'] || customer.customerName || 'İsimsiz';
          
          // Debug: Vahap Çeliker için detaylı log
          if (customerName.toLowerCase().includes('vahap')) {
            console.log(`🎯 Vahap Çeliker için debt verisi çekiliyor...`);
            console.log(`  - Customer ID: ${customer.id}`);
            console.log(`  - Customer Name: ${customerName}`);
            console.log(`  - Path: users/${userEmail}/customerList/${customer.id}/debt/current`);
          }
          
          const debtRef = fbDoc(db, `users/${userEmail}/customerList/${customer.id}/debt/current`);
          const debtDoc = await getDoc(debtRef);
          
          if (debtDoc.exists()) {
            const debtData = debtDoc.data();
            const debtHistory = debtData.history || [];
            
            // Debug: Vahap Çeliker için borç verisi
            if (customerName.toLowerCase().includes('vahap')) {
              console.log(`✅ Vahap Çeliker borç verisi bulundu:`, debtData);
              console.log(`  - Amount: ${debtData.amount}`);
              console.log(`  - History count: ${debtHistory.length}`);
            }
            
            let lastTransactionDate = null;
            if (debtHistory.length > 0) {
              const realTransactions = debtHistory.filter(h =>
                !h.isInfoOnly &&
                !(h.transferType && h.transferType.startsWith('puantaj_')) &&
                parseFloat(h.amount || 0) !== 0
              );
              if (realTransactions.length > 0) {
                lastTransactionDate = realTransactions.reduce((latest, current) => {
                  const latestDateStr = latest.transferredFrom ? (latest.originalDate || latest.date) : latest.date;
                  const currentDateStr = current.transferredFrom ? (current.originalDate || current.date) : current.date;
                  const latestDate = parseFlexible(latestDateStr) || new Date(0);
                  const currentDate = parseFlexible(currentDateStr) || new Date(0);
                  return currentDate > latestDate ? current : latest;
                });
                lastTransactionDate = lastTransactionDate.transferredFrom
                  ? (lastTransactionDate.originalDate || lastTransactionDate.date)
                  : lastTransactionDate.date;
              }
            }
            
                      // HopsPlus mantığı: Sadece amount'ları topla (type'a bakmadan)
          const computedAmount = (() => {
            // Önce Firebase'deki amount'ı kontrol et
            if (typeof debtData.amount === 'number' && debtData.amount !== 0) return debtData.amount;
            
            // Eğer amount 0 ise veya yoksa, history'den hesapla
            if (debtHistory.length === 0) return 0;
            
            // HopsPlus mantığı: Tüm amount'ları topla (isInfoOnly olanları hariç)
            return debtHistory
              .filter(h => !h.isInfoOnly) // Sadece bilgi amaçlı olanları hariç tut
              .reduce((tot, h) => {
                const amount = parseFloat(h.amount || 0);
                return tot + amount; // Direkt topla, type'a bakma
              }, 0);
          })();
            return {
              customerId: customer.id,
              data: {
                amount: computedAmount,
                history: debtData.history || [],
                lastUpdated: debtData.lastUpdated,
                loading: false,
                lastTransactionDate: lastTransactionDate
              }
            };
          } else {
            // Debug: Vahap Çeliker için borç belgesi yoksa
            const customerName = customer['Müşteri Adı'] || customer.customerName || 'İsimsiz';
            if (customerName.toLowerCase().includes('vahap')) {
              console.log(`❌ Vahap Çeliker için borç belgesi bulunamadı!`);
              console.log(`  - Customer ID: ${customer.id}`);
              console.log(`  - Customer Name: ${customerName}`);
              console.log(`  - Belge yolu: users/${userEmail}/customerList/${customer.id}/debt/current`);
            }
            
            return {
              customerId: customer.id,
              data: {
                amount: 0,
                history: [],
                lastUpdated: null,
                loading: false,
                lastTransactionDate: null
              }
            };
          }
        } catch (error) {
          const customerName = customer['Müşteri Adı'] || customer.customerName || 'İsimsiz';
          console.error(`Müşteri ${customerName} borç bilgisi alınamadı:`, error);
          
          // Debug: Vahap Çeliker için hata
          if (customerName.toLowerCase().includes('vahap')) {
            console.error(`🔴 Vahap Çeliker için HATA:`, error);
          }
          return {
            customerId: customer.id,
            data: {
              amount: 0,
              history: [],
              lastUpdated: null,
              loading: false,
              lastTransactionDate: null
            }
          };
        }
      });
      
      // Bu batch'in sonuçlarını bekle
      const batchResults = await Promise.all(batchPromises);
      
      // Sonuçları state'e ekle
      setCustomerDebts(prevDebts => {
        const newDebts = { ...prevDebts };
        batchResults.forEach(result => {
          newDebts[result.customerId] = result.data;
        });
        return newDebts;
      });
      
      // UI'nin güncellenebilmesi için kısa bir bekleme
      if (i + batchSize < customers.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    const endTime = Date.now();
    console.log(`✅ Borç bilgileri yüklendi: ${endTime - startTime}ms (${customers.length} müşteri)`);
  };

  // Banka listesini getir
  useEffect(() => {
    const db = getFirestore();
    const currentUser = auth.currentUser;

    if (!currentUser?.email) {
      return;
    }

    const bankalarRef = collection(db, `users/${currentUser.email}/bankalar`);
    
    const unsubscribe = onSnapshot(bankalarRef, (snapshot) => {
      const bankaData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setBankalar(bankaData);
    });

    return () => unsubscribe();
  }, []);

  // filteredCariList kaldırıldı - artık sortedCariList kullanılıyor

  // Sıralanmış ve filtrelenmiş cari listesi
  const sortedCariList = useMemo(() => {
    const finalList = cariList.filter(cari => {
        const isSelected = selectedCariList.some(c => c.id === cari.id);
        const debtInfo = customerDebts[cari.id];
        
        // İşlem geçmişi var mı kontrolü
        const hasTransactions = debtInfo && debtInfo.history && debtInfo.history.length > 0;
        
        // Türkçe karakterlere uygun arama - utility kullan
        const searchMatch = turkishMultiSearch(searchTerm, [
          cari['Müşteri Adı'] || '',
          cari.cariCode || ''
        ]);

        // Son işlem tarihi filtresi kontrolü
        let lastTransactionDateMatch = true;
        if (selectedLastTransactionDays && debtInfo && debtInfo.lastTransactionDate) {
          const lastTransactionDate = parseFlexible(debtInfo.lastTransactionDate) || new Date(0);
          const currentDate = new Date();
          
          // Sadece tarih kısmını karşılaştır (saat farkını göz ardı et)
          const lastTransactionDateOnly = new Date(lastTransactionDate.getFullYear(), lastTransactionDate.getMonth(), lastTransactionDate.getDate());
          const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          
          const daysDifference = Math.floor((currentDateOnly - lastTransactionDateOnly) / (1000 * 60 * 60 * 24));
          
          // Debug için konsol log'u
          console.log(`🗓️ Müşteri: ${cari['Müşteri Adı']}, Son işlem: ${lastTransactionDate.toLocaleDateString()}, Bugün: ${currentDate.toLocaleDateString()}, Gün farkı: ${daysDifference}, Filtre: ${selectedLastTransactionDays.value} gün`);
          
          // Filtreleme mantığı:
          // - "Bugün" (value: 0): sadece bugün (daysDifference = 0)
          // - "Son 2 gün" (value: 2): bugün + dün (daysDifference <= 1)
          // - "Son 3 gün" (value: 3): bugün + dün + önceki gün (daysDifference <= 2)
          // - "Son N gün" (value: N): son N-1 gün (daysDifference <= N-1)
          
          if (selectedLastTransactionDays.value === 0) {
            // "Bugün" seçilirse sadece bugün
            lastTransactionDateMatch = daysDifference === 0;
          } else {
            // "Son N gün" seçilirse son N-1 gün (bugün dahil)
            lastTransactionDateMatch = daysDifference <= (selectedLastTransactionDays.value - 1);
          }
        } else if (selectedLastTransactionDays) {
          // Filtre seçili ama son işlem tarihi yoksa gösterme
          lastTransactionDateMatch = false;
        }

        // Bakiye türü filtresi kontrolü
        let balanceTypeMatch = true;
        if (selectedBalanceType && debtInfo) {
          const balance = debtInfo.amount || 0;
          
          if (selectedBalanceType.value === 'alacakli') {
            // Alacaklı: - bakiye (negatif)
            balanceTypeMatch = balance < 0;
          } else if (selectedBalanceType.value === 'borclu') {
            // Borçlu: + bakiye (pozitif)
            balanceTypeMatch = balance > 0;
          }
        }

        // Kural 1: Seçiliyse (yapışkansa) her zaman göster.
        if (isSelected) {
            return true;
        }

        // Kural 2: Seçili değilse ve işlemi yoksa, gösterme.
        // Ancak arama filtresi kullanılıyorsa (searchTerm boş değilse) işlemi olmayan müşterileri de göster
        // `loading` durumunu da kontrol ederek, veri yüklenirken müşterinin kaybolmasını önle
        if (!hasTransactions && debtInfo && debtInfo.loading === false) {
            // Eğer arama filtresi kullanılıyorsa işlemi olmayan müşterileri de göster
            if (searchTerm.trim() === '') {
                return false;
            }
        }
        
        // Kural 3: Diğer durumlarda (işlemi olanlar veya verisi henüz yüklenmeyenler), arama filtresine, son işlem tarihi filtresine ve bakiye türü filtresine uymalı.
        return searchMatch && lastTransactionDateMatch && balanceTypeMatch;
    });

    // Sıralama mantığını düzelt
    const comparator = (a, b) => {
      let valA, valB;

      if (orderBy === 'Bakiye') {
        // Bakiye sıralaması için customerDebts'ten sayısal değerleri al
        valA = customerDebts[a.id]?.amount || 0;
        valB = customerDebts[b.id]?.amount || 0;
      } else if (orderBy === 'Son İşlem') {
        valA = customerDebts[a.id]?.lastTransactionDate ? (parseFlexible(customerDebts[a.id].lastTransactionDate) || new Date(0)) : new Date(0);
        valB = customerDebts[b.id]?.lastTransactionDate ? (parseFlexible(customerDebts[b.id].lastTransactionDate) || new Date(0)) : new Date(0);
      } else {
        // Diğer sütunlar için metin tabanlı sıralama
        valA = a[orderBy] || '';
        valB = b[orderBy] || '';
      }

      if (valA < valB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };
    
    const sorted = [...finalList].sort(comparator);

    // === DEVİRSEL YENİLİK: Seçili satırları FİZİKSEL olarak en üste taşı ===
    const selectedIds = new Set(selectedCariList.map(c => c.id));
    const pinned = sorted.filter(c => selectedIds.has(c.id));
    const unpinned = sorted.filter(c => !selectedIds.has(c.id));

    return [...pinned, ...unpinned];
  }, [cariList, searchTerm, order, orderBy, selectedCariList, customerDebts, selectedLastTransactionDays, selectedBalanceType]);

  // filteredTransactions kaldırıldı - her cari için ayrı filtreleme yapılıyor

  // Seçili cariler değiştiğinde gerçek borç verilerini yükle
  useEffect(() => {
    selectedCariList.forEach(selectedCari => {
      if (selectedCari && customerDebts[selectedCari.id] && !cariTransactions[selectedCari.id]) {
        console.log('🏦 Cari seçildi:', selectedCari['Müşteri Adı']);
        
        const debtData = customerDebts[selectedCari.id];
        const history = debtData.history || [];
        
        // History verilerini işlem formatına çevir
        const formattedTransactions = history
          .filter((historyItem) =>
            !historyItem.isInfoOnly && // Bilgi amaçlı satırları gösterme
            !(historyItem.transferType && historyItem.transferType.startsWith('puantaj_')) // Puantaj_existing / puantaj_merge satırlarını gösterme
          )
          .map((historyItem, index) => {
            // Gerçek history index'ini bul
            const realHistoryIndex = history.findIndex(item => 
              item.id === historyItem.id &&
              item.date === historyItem.date &&
              item.amount === historyItem.amount
            );
            
            return {
              id: historyItem.id || `${selectedCari.id}_${index}`,
              date: historyItem.date || toIsoUtcString(new Date()).slice(0, 10),
              paymentMethod: (historyItem.transferredFrom || historyItem.transferFrom)
                ? 'Puantaj + Aktarım'
                : getTransactionTypeLabel(historyItem.type),
              description: historyItem.description || 'Açıklama yok',
              // HopsPlus mantığı: amount alanını koru
              amount: historyItem.amount || 0,
              // Görünüm için alacak/borç hesapla
              alacak: (historyItem.amount < 0) ? Math.abs(historyItem.amount) : 0,
              borc: (historyItem.amount > 0) ? Math.abs(historyItem.amount) : 0,
              createdAt: parseFlexible(historyItem.date) || new Date(),
              machineHours: historyItem.machineHours || 0,
              hourlyRate: historyItem.hourlyRate || 0,
              machineName: historyItem.machineName || '',
              type: historyItem.type || 'increase',
              transferFrom: historyItem.transferFrom || historyItem.transferredFrom,
              transferTo: historyItem.transferTo,
              isTransferred: historyItem.transferredFrom ? true : false,
              transferOrigin: historyItem.transferFrom || historyItem.transferredFrom,
              isInfoOnly: historyItem.isInfoOnly || false,
              editFlag: historyItem.editFlag || false,
              editDetails: historyItem.editDetails || null,
              realHistoryIndex: realHistoryIndex
            };
          });
        
        // Özel sıralama: Aynı gün içinde borçlar alacaklardan önce gelsin
        formattedTransactions.sort((a, b) => compareTransactions(a, b, true));
        
        // Bu carinin transactions'ını kaydet
        setCariTransactions(prev => ({
          ...prev,
          [selectedCari.id]: formattedTransactions
        }));
      }
    });
  }, [selectedCariList, customerDebts, cariTransactions]);

  // Seçili carilerin borç bilgilerini real-time dinle - SADECE SEÇİLİ MÜŞTERİLER İÇİN
  useEffect(() => {
    if (!selectedCariList.length || !auth.currentUser?.email) return;

    console.log('🎯 Real-time dinleyiciler başlatılıyor:', selectedCariList.map(c => c['Müşteri Adı']));
    const db = getFirestore();
    const unsubscribes = [];
    
    selectedCariList.forEach(selectedCari => {
      const debtRef = fbDoc(db, `users/${auth.currentUser.email}/customerList/${selectedCari.id}/debt/current`);
      
      const unsubscribe = onSnapshot(debtRef, (doc) => {
        if (doc.exists()) {
          const debtData = doc.data();
          const debtHistory = debtData.history || [];
          let lastTransactionDate = null;
          if (debtHistory.length > 0) {
            const realTransactions = debtHistory.filter(h =>
              !h.isInfoOnly &&
              !(h.transferType && h.transferType.startsWith('puantaj_')) &&
              parseFloat(h.amount || 0) !== 0
            );
            if (realTransactions.length > 0) {
              const latest = realTransactions.reduce((lat, cur) => {
                const latStr = lat.transferredFrom ? (lat.originalDate || lat.date) : lat.date;
                const curStr = cur.transferredFrom ? (cur.originalDate || cur.date) : cur.date;
                return (parseFlexible(curStr) || new Date(0)) > (parseFlexible(latStr) || new Date(0)) ? cur : lat;
              });
              lastTransactionDate = latest.transferredFrom
                ? (latest.originalDate || latest.date)
                : latest.date;
            }
          }
          
          // CustomerDebts state'ini güncelle
          setCustomerDebts(prev => ({
            ...prev,
            [selectedCari.id]: {
              amount: (typeof debtData.amount === 'number' ? debtData.amount : 0),
              history: debtData.history || [],
              lastUpdated: debtData.lastUpdated,
              loading: false,
              lastTransactionDate: lastTransactionDate
            }
          }));
          
          // Bu carinin transactions'ını da güncelle
          const history = debtData.history || [];
          const formattedTransactions = history
            .filter((historyItem) =>
              !historyItem.isInfoOnly && // Bilgi amaçlı satırları gösterme
              !(historyItem.transferType && historyItem.transferType.startsWith('puantaj_')) // Puantaj_existing / puantaj_merge satırlarını gösterme
            )
            .map((historyItem, index) => {
              // Gerçek history index'ini bul
              const realHistoryIndex = history.findIndex(item => 
                item.id === historyItem.id &&
                item.date === historyItem.date &&
                item.amount === historyItem.amount
              );
              
              return {
                id: historyItem.id || `${selectedCari.id}_${index}`,
                date: historyItem.date || toIsoUtcString(new Date()).slice(0, 10),
                paymentMethod: (historyItem.transferredFrom || historyItem.transferFrom)
                  ? 'Puantaj + Aktarım'
                  : getTransactionTypeLabel(historyItem.type),
                description: historyItem.description || 'Açıklama yok',
                // HopsPlus mantığı: amount alanını koru
                amount: historyItem.amount || 0,
                // Görünüm için alacak/borç hesapla
                alacak: (historyItem.amount < 0) ? Math.abs(historyItem.amount) : 0,
                borc: (historyItem.amount > 0) ? Math.abs(historyItem.amount) : 0,
                createdAt: parseFlexible(historyItem.date) || new Date(),
                machineHours: historyItem.machineHours || 0,
                hourlyRate: historyItem.hourlyRate || 0,
                machineName: historyItem.machineName || '',
                type: historyItem.type || 'increase',
                transferFrom: historyItem.transferFrom || historyItem.transferredFrom,
                transferTo: historyItem.transferTo,
                isTransferred: historyItem.transferredFrom ? true : false,
                transferOrigin: historyItem.transferFrom || historyItem.transferredFrom,
                isInfoOnly: historyItem.isInfoOnly || false,
                isEdited: historyItem.isEdited || false,
                editCount: historyItem.editCount || 0,
                originalAmount: historyItem.originalAmount || historyItem.amount,
                originalDate: historyItem.originalDate || historyItem.date,
                originalDescription: historyItem.originalDescription || historyItem.description,
                updatedAt: historyItem.updatedAt || historyItem.date,
                editFlag: historyItem.editFlag || false,
                editDetails: historyItem.editDetails || null,
                realHistoryIndex: realHistoryIndex
              };
            });
          
          // Özel sıralama: İlk girişten son girişe doğru (en eski en üstte) ve aynı gün içinde alacaklar en altta
          formattedTransactions.sort(compareTransactions);
          
          setCariTransactions(prev => ({
            ...prev,
            [selectedCari.id]: formattedTransactions
          }));
          
          console.log('🏦 Real-time borç verisi güncellendi:', selectedCari['Müşteri Adı'], debtData.amount);
        }
      }, (error) => {
        console.error('Borç verisi dinlenirken hata:', error);
      });
      
      unsubscribes.push(unsubscribe);
    });

    return () => {
      console.log('🎯 Real-time dinleyiciler temizlendi:', selectedCariList.map(c => c['Müşteri Adı']));
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [selectedCariList]);

  // İşlem türü etiketleri
  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'increase':
        return 'Puantaj';
      case 'decrease':
        return 'Ödeme';
      case 'transfer_in':
        return 'Transfer (Gelen)';
      case 'transfer_out':
        return 'Transfer (Giden)';
      default:
        return 'Belirtilmemiş';
    }
  };

  // İşlem satırı CSS sınıfları
  const getTransactionRowClass = (type) => {
    switch (type) {
      case 'increase':
        return 'debt-increase';
      case 'decrease':
        return 'debt-decrease';
      case 'transfer_in':
        return 'debt-transfer-in';
      case 'transfer_out':
        return 'debt-transfer-out';
      default:
        return 'debt-increase';
    }
  };

  // Hesaplama fonksiyonları
  const calculateTotalDebt = (transactions) => {
    if (!transactions) return 0;
    return transactions.reduce((total, t) => total + (t.borc || 0), 0);
  };

  const calculateTotalCredit = (transactions) => {
    if (!transactions) return 0;
    return transactions.reduce((total, t) => total + (t.alacak || 0), 0);
  };

  // HopsPlus mantığı: Direkt history amount'larını topla
  const calculateBalance = (transactions) => {
    if (!transactions) return 0;
    
    // HopsPlus mantığı: isInfoOnly olmayan tüm amount'ları topla
    return transactions
      .filter(t => !t.isInfoOnly)
      .reduce((total, t) => {
        // amount alanını kullan, yoksa borc - alacak hesabı yap
        if (t.amount !== undefined) {
          return total + parseFloat(t.amount || 0);
        }
        // Fallback: eski mantık
        return total + (t.borc || 0) - (t.alacak || 0);
      }, 0);
  };

  const calculateBalanceForTransaction = (sortedForBalance, transaction) => {
    const currentTransactionDate = parseFlexible(transaction.date) || new Date(0);
    const transactionsUpToThis = sortedForBalance.filter(t => 
      (parseFlexible(t.date) || new Date(0)) <= currentTransactionDate
    );
    return calculateBalance(transactionsUpToThis);
  };

  // Tüm carilerin toplam bakiyesini hesapla
  const calculateTotalCariBalance = () => {
    let totalBalance = 0;
    
    // Tüm carilerin bakiyelerini topla
    Object.values(customerDebts).forEach(debtData => {
      if (debtData && typeof debtData.amount === 'number' && !debtData.loading) {
        totalBalance += debtData.amount;
      }
    });
    
    return totalBalance;
  };

  // Filtrelenmiş carilerin toplam bakiyesini hesapla
  const calculateFilteredTotalCariBalance = () => {
    let totalBalance = 0;
    
    // Sadece filtrelenmiş carilerin bakiyelerini topla
    sortedCariList.forEach(cari => {
      const debtData = customerDebts[cari.id];
      if (debtData && typeof debtData.amount === 'number' && !debtData.loading) {
        totalBalance += debtData.amount;
      }
    });
    
    return totalBalance;
  };

  // Filtre uygulanıp uygulanmadığını kontrol et
  const hasActiveFilters = () => {
    return searchTerm || 
           selectedLastTransactionDays || 
           selectedBalanceType;
  };

  const handleCariSelect = (cari) => {
    // Tek bir cari objesi geliyor
    setSelectedCariList(prevSelected => {
      const isAlreadySelected = prevSelected.some(selected => selected.id === cari.id);
      
      if (isAlreadySelected) {
        // Eğer zaten seçiliyse, listeden çıkar
        const newSelected = prevSelected.filter(selected => selected.id !== cari.id);
        
        // Bu carinin transactions'ını da temizle
        setCariTransactions(prevTransactions => {
          const newTransactions = { ...prevTransactions };
          delete newTransactions[cari.id];
          return newTransactions;
        });
        
        return newSelected;
      } else {
        // Eğer seçili değilse, listeye ekle
        const newSelected = [...prevSelected, cari];
        
        // Yeni seçilen cari için transactions'ı yükle
        if (!cariTransactions[cari.id]) {
          const debtData = customerDebts[cari.id];
          if (debtData && debtData.history) {
            const formattedTransactions = debtData.history
              .filter((historyItem) =>
                !historyItem.isInfoOnly && // Bilgi amaçlı satırları gösterme
                !(historyItem.transferType && historyItem.transferType.startsWith('puantaj_')) // Puantaj_existing / puantaj_merge satırlarını gösterme
              )
              .map((historyItem, index) => ({
                id: `${cari.id}_${index}`,
                date: historyItem.date || toIsoUtcString(new Date()).slice(0, 10),
                paymentMethod: (historyItem.transferredFrom || historyItem.transferFrom)
                  ? 'Puantaj + Aktarım'
                  : getTransactionTypeLabel(historyItem.type),
                description: historyItem.description || 'Açıklama yok',
                // HopsPlus mantığı: amount alanını koru
                amount: historyItem.amount || 0,
                // Görünüm için alacak/borç hesapla
                alacak: (historyItem.amount < 0) ? Math.abs(historyItem.amount) : 0,
                borc: (historyItem.amount > 0) ? Math.abs(historyItem.amount) : 0,
                createdAt: parseFlexible(historyItem.date) || new Date(),
                machineHours: historyItem.machineHours || 0,
                hourlyRate: historyItem.hourlyRate || 0,
                machineName: historyItem.machineName || '',
                type: historyItem.type || 'increase',
                transferFrom: historyItem.transferFrom || historyItem.transferredFrom,
                transferTo: historyItem.transferTo,
                isTransferred: historyItem.transferredFrom ? true : false,
                transferOrigin: historyItem.transferFrom || historyItem.transferredFrom,
                isInfoOnly: historyItem.isInfoOnly || false,
                isEdited: historyItem.isEdited || false,
                editCount: historyItem.editCount || 0,
                originalAmount: historyItem.originalAmount || historyItem.amount,
                originalDate: historyItem.originalDate || historyItem.date,
                originalDescription: historyItem.originalDescription || historyItem.description,
                updatedAt: historyItem.updatedAt || historyItem.date,
                editFlag: historyItem.editFlag || false,
                editDetails: historyItem.editDetails || null
              }));
            
            // Özel sıralama: İlk girişten son girişe doğru (en eski en üstte) ve aynı gün içinde alacaklar en altta
            formattedTransactions.sort(compareTransactions);
            
            setCariTransactions(prev => ({
              ...prev,
              [cari.id]: formattedTransactions
            }));
          }
        }
        
        return newSelected;
      }
    });
  };

  // Filtreleri temizleme fonksiyonu
  const clearFilters = () => {
    setSelectedLastTransactionDays(null);
    setSelectedBalanceType(null);
  };

  // Cari seçimini kaldırma fonksiyonu
  const handleCariRemove = (cariToRemove) => {
    setSelectedCariList(prevSelected => {
      const newSelected = prevSelected.filter(cari => cari.id !== cariToRemove.id);
      
      // Bu carinin transactions'ını da temizle
      setCariTransactions(prev => {
        const newTransactions = { ...prev };
        delete newTransactions[cariToRemove.id];
        return newTransactions;
      });
      
      // Bu carinin filtrelerini de temizle
      clearCariFilters(cariToRemove.id);
      
      console.log('🗑️ Cari seçimden kaldırıldı:', cariToRemove['Müşteri Adı']);
      
      return newSelected;
    });
  };

  // Modal fonksiyonları
  const openAddTransactionModal = (cari) => {
    if (!selectedCariList.length) {
      alert('Lütfen önce bir müşteri seçin');
      return;
    }
    
    // İlk seçili müşterinin bilgilerini kullan
    setNewTransaction({
      customerName: cari['Müşteri Adı'],
      cariCode: cari.cariCode,
      transactionType: 'Borç',
      amount: '',
      date: toIsoUtcString(new Date()).slice(0, 10),
      description: '',
      selectedCariId: cari.id
    });
    setShowAddTransactionModal(true);
  };

  const closeAddTransactionModal = () => {
    setShowAddTransactionModal(false);
    setNewTransaction({
      customerName: '',
      cariCode: '',
      transactionType: 'Borç',
      amount: '',
      date: toIsoUtcString(new Date()).slice(0, 10),
      description: '' // Açıklama alanı
    });
  };

  const handleTransactionInputChange = (field, value) => {
    setNewTransaction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openAddCreditModal = (cari) => {
    if (!selectedCariList.length) {
      alert('Lütfen önce bir müşteri seçin');
      return;
    }
    
    // İlk seçili müşterinin bilgilerini kullan
    setNewCredit({
      customerName: cari['Müşteri Adı'],
      cariCode: cari.cariCode,
      paymentDescription: '',
      amount: '',
      date: toIsoUtcString(new Date()).slice(0, 10),
      description: '',
      selectedCariId: cari.id
    });
    setShowAddCreditModal(true);
  };

  const closeAddCreditModal = () => {
    setShowAddCreditModal(false);
    setNewCredit({
      customerName: '',
      cariCode: '',
      paymentDescription: '',
      amount: '',
      date: toIsoUtcString(new Date()).slice(0, 10),
      description: ''
    });
  };

  const handleCreditInputChange = (field, value) => {
    setNewCredit(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCredit = async () => {
    if (!newCredit.selectedCariId || !auth.currentUser?.email) return;

    const selectedCari = cariList.find(cari => cari.id === newCredit.selectedCariId);
    if (!selectedCari) {
      console.error('Seçili cari bulunamadı');
      return;
    }

    const creditAmount = parseFloat(newCredit.amount) || 0;
    if (creditAmount <= 0) return;

    try {
      const db = getFirestore();
      const userEmail = auth.currentUser.email;
      
      // Müşteri borç referansı
      const debtRef = fbDoc(db, `users/${userEmail}/customerList/${selectedCari.id}/debt/current`);
      
      // Transaction kullanarak atomik işlem yap
      await runTransaction(db, async (transaction) => {
        const debtDoc = await transaction.get(debtRef);
        
        // Mevcut borç miktarını al
        const currentAmount = debtDoc.exists() ? (debtDoc.data().amount || 0) : 0;
        
        // Yeni borç miktarını hesapla (alacak eklendiğinde borç azalır)
        const newAmount = currentAmount - creditAmount; // Negatif olabilir
        
        // Kullanıcının seçtiği tarihi kullan (local timezone ile)
        const currentTimeString = toIsoUtcString(parseFlexible(newCredit.date) || new Date());
        
        console.log('🗓️ Alacak ekleme - Seçilen tarih:', newCredit.date);
        console.log('🗓️ Alacak ekleme - ISO tarih:', currentTimeString);
        
        // Mevcut history'yi al
        const currentHistory = debtDoc.exists() ? (debtDoc.data().history || []) : [];
        
        // Yeni history entry'si oluştur
        const newHistoryEntry = {
          id: `credit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Benzersiz ID
          amount: -creditAmount, // Negatif değer (alacak)
          date: currentTimeString,
          description: newCredit.description || `${newCredit.paymentDescription} - Alacak`,
          paymentMethod: newCredit.paymentDescription,
          type: 'decrease'
        };
        
        // History listesini güncelle
        const updatedHistory = [...currentHistory, newHistoryEntry];
        
        // Borç bilgilerini güncelle
        const updateData = {
          amount: newAmount,
          history: updatedHistory,
          lastUpdated: currentTimeString,
          customerName: selectedCari['Müşteri Adı']
        };
        
        // Firebase'e kaydet
        transaction.set(debtRef, updateData);
        
        console.log('🏦 Alacak eklendi:', {
          eskiBorç: currentAmount,
          alacakMiktarı: creditAmount,
          yeniBorç: newAmount
        });
      });
      
      closeAddCreditModal();
      
      // Başarı popup'ını göster
      const transactionInfo = {
        type: 'Alacak',
        amount: creditAmount,
        customerName: selectedCari['Müşteri Adı'],
        description: newCredit.description || `${newCredit.paymentDescription} - Alacak`,
        paymentMethod: newCredit.paymentDescription,
        date: formatTrDateOnly(newCredit.date)
      };
      
      showAddSuccessMessage(transactionInfo);
      
    } catch (error) {
      console.error('Alacak eklenirken hata:', error);
      alert('Alacak eklenirken bir hata oluştu: ' + error.message);
    }
  };

  const handleAddTransaction = async () => {
    if (!newTransaction.selectedCariId || !auth.currentUser?.email) return;

    const selectedCari = cariList.find(cari => cari.id === newTransaction.selectedCariId);
    if (!selectedCari) {
      console.error('Seçili cari bulunamadı');
      return;
    }

    const debtAmount = parseFloat(newTransaction.amount) || 0;
    if (debtAmount <= 0) return;

    try {
      const db = getFirestore();
      const userEmail = auth.currentUser.email;
      
      // Müşteri borç referansı
      const debtRef = fbDoc(db, `users/${userEmail}/customerList/${selectedCari.id}/debt/current`);
      
      // Transaction kullanarak atomik işlem yap
      await runTransaction(db, async (transaction) => {
        const debtDoc = await transaction.get(debtRef);
        
        // Mevcut borç miktarını al
        const currentAmount = debtDoc.exists() ? (debtDoc.data().amount || 0) : 0;
        
        // Yeni borç miktarını hesapla (borç eklendiğinde borç artar)
        const newAmount = currentAmount + debtAmount;
        
        // Kullanıcının seçtiği tarihi kullan (local timezone ile)
        const currentTimeString = toIsoUtcString(parseFlexible(newTransaction.date) || new Date());
        
        console.log('🗓️ Borç ekleme - Seçilen tarih:', newTransaction.date);
        console.log('🗓️ Borç ekleme - ISO tarih:', currentTimeString);
        
        // Mevcut history'yi al
        const currentHistory = debtDoc.exists() ? (debtDoc.data().history || []) : [];
        
        // Yeni history entry'si oluştur
        const newHistoryEntry = {
          id: `debt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Benzersiz ID
          amount: debtAmount, // Pozitif değer (borç)
          date: currentTimeString,
          description: newTransaction.description || (newTransaction.paymentDescription ? `${newTransaction.paymentDescription} - Borç` : 'Borç İşlemi'),
          paymentMethod: newTransaction.paymentDescription || 'Borç',
          type: 'increase'
        };
        
        // History listesini güncelle
        const updatedHistory = [...currentHistory, newHistoryEntry];
        
        // Borç bilgilerini güncelle
        const updateData = {
          amount: newAmount,
          history: updatedHistory,
          lastUpdated: currentTimeString,
          customerName: selectedCari['Müşteri Adı']
        };
        
        // Firebase'e kaydet
        transaction.set(debtRef, updateData);
        
        console.log('🏦 Borç eklendi:', {
          eskiBorç: currentAmount,
          borcMiktarı: debtAmount,
          yeniBorç: newAmount
        });
      });
      
      closeAddTransactionModal();
      
      // Başarı popup'ını göster
      const transactionInfo = {
        type: 'Borç',
        amount: debtAmount,
        customerName: selectedCari['Müşteri Adı'],
        description: newTransaction.description || (newTransaction.paymentDescription ? `${newTransaction.paymentDescription} - Borç` : 'Borç İşlemi'),
        paymentMethod: newTransaction.paymentDescription || 'Borç',
        date: formatTrDateOnly(newTransaction.date)
      };
      
      showAddSuccessMessage(transactionInfo);
      
    } catch (error) {
      console.error('Borç eklenirken hata:', error);
      alert('Borç eklenirken bir hata oluştu: ' + error.message);
    }
  };

  // İşlem düzenleme modalını aç
  const openEditTransactionModal = (transaction, type) => {
    // Transaction objesindeki amount değerini kullan
    const amount = Math.abs(parseFloat(transaction.amount || 0));
    
    setEditingTransaction({
      ...transaction,
      transactionType: type, // Parametre olarak gelen işlem tipi
      amount: amount.toString(), // Mutlak değeri string olarak kaydet
      originalAmount: transaction.amount, // Orijinal tutarı sakla
      borc: transaction.borc || 0,
      alacak: transaction.alacak || 0,
      customerId: transaction.customerId || transaction.selectedCariId, // customerId alanını ekle
      description: transaction.description || transaction.paymentDescription || ''
    });
    setShowEditTransactionModal(true);
  };

  // İşlem düzenleme modalını kapat
  const closeEditTransactionModal = () => {
    setEditingTransaction(null);
    setShowEditTransactionModal(false);
  };

  // İşlem düzenleme kaydet
  const handleEditTransaction = async (updatedTransaction) => {
    try {
      console.log('🔄 İşlem düzenleniyor:', updatedTransaction);
      
      const db = getFirestore();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error('Kullanıcı oturumu bulunamadı');
        return;
      }

      const customerId = updatedTransaction.customerId;
      const debtRef = fbDoc(db, `users/${currentUser.email}/customerList/${customerId}/debt/current`);

      await runTransaction(db, async (transaction) => {
        const debtDoc = await transaction.get(debtRef);
        
        if (!debtDoc.exists()) {
          throw new Error('Borç belgesi bulunamadı');
        }

        const debtData = debtDoc.data();
        const history = debtData.history || [];
        
        console.log('📊 Mevcut history:', history);
        console.log('🎯 Düzenlenecek işlem ID:', updatedTransaction.id);

        // Gerçek history index'ini kullan
        let transactionIndex = updatedTransaction.realHistoryIndex;
        
        console.log('📍 Düzenlenecek işlem index:', transactionIndex);
        
        // Index geçerli mi kontrol et
        if (transactionIndex === undefined || transactionIndex === -1 || transactionIndex >= history.length) {
          console.error('❌ İşlem history\'de bulunamadı:', updatedTransaction);
          console.log('🔍 Aranan işlem detayları:', {
            id: updatedTransaction.id,
            realHistoryIndex: updatedTransaction.realHistoryIndex,
            date: updatedTransaction.date,
            description: updatedTransaction.description,
            amount: updatedTransaction.amount,
            transactionType: updatedTransaction.transactionType,
            customerId: updatedTransaction.customerId
          });
          console.log('📋 Mevcut history detayları:', history.map((item, idx) => ({
            index: idx,
            id: item.id,
            date: item.date,
            description: item.description,
            amount: item.amount,
            type: item.type
          })));
          
          throw new Error(`İşlem history'de bulunamadı. ID: ${updatedTransaction.id}, RealHistoryIndex: ${updatedTransaction.realHistoryIndex}, Tarih: ${updatedTransaction.date}, Tutar: ${updatedTransaction.amount}`);
        }

        // İşlem tipine göre Firebase'e kaydedilecek amount değerini belirle
        const amount = Math.abs(parseFloat(updatedTransaction.amount));
        
        // Firebase'deki timeline mantığı:
        // - increase (Borç): pozitif değer
        // - decrease (Alacak): negatif değer
        let finalAmount;
        let newType;
        
        if (updatedTransaction.transactionType === 'Borç') {
          finalAmount = amount;  // Pozitif
          newType = 'increase';
        } else {
          finalAmount = -amount; // Negatif
          newType = 'decrease';
        }

        console.log('💰 Hesaplanan değerler:', {
          originalAmount: updatedTransaction.amount,
          transactionType: updatedTransaction.transactionType,
          finalAmount: finalAmount,
          newType: newType
        });

        // History'deki işlemi index ile bul ve güncelle
        const updatedHistory = history.map((item, index) => {
          if (index === transactionIndex) {
            // Düzenleme detaylarını oluştur - sadece geçerli alanları ekle
            const editDetails = {
              eski: {
                amount: item.amount,
                date: item.date,
                description: item.description,
                type: item.type
              },
              yeni: {
                amount: finalAmount,
                date: updatedTransaction.date,
                description: updatedTransaction.description || '',
                type: newType
              },
              duzenleyen: auth.currentUser?.email || 'Bilinmiyor',
              duzenlemeTarihi: toIsoUtcString(new Date())
            };
            
            // Güncellenmiş item'ı oluştur - sadece Firebase'de var olan alanları ekle
            const updatedItem = {
              amount: finalAmount,
              date: updatedTransaction.date,
              description: updatedTransaction.description || '',
              type: newType
            };
            
            // Orijinal item'dan geçerli alanları kopyala (sadece Firebase'de var olanlar)
            if (item.hourlyRate !== undefined) updatedItem.hourlyRate = item.hourlyRate;
            if (item.machineHours !== undefined) updatedItem.machineHours = item.machineHours;
            if (item.machineName !== undefined) updatedItem.machineName = item.machineName;
            if (item.puantajCode !== undefined) updatedItem.puantajCode = item.puantajCode;
            if (item.id !== undefined) updatedItem.id = item.id;
            if (item.transferFrom !== undefined) updatedItem.transferFrom = item.transferFrom;
            if (item.transferTo !== undefined) updatedItem.transferTo = item.transferTo;
            if (item.transferredAt !== undefined) updatedItem.transferredAt = item.transferredAt;
            if (item.transferredBy !== undefined) updatedItem.transferredBy = item.transferredBy;
            if (item.transferOrigin !== undefined) updatedItem.transferOrigin = item.transferOrigin;
            if (item.isInfoOnly !== undefined) updatedItem.isInfoOnly = item.isInfoOnly;
            if (item.paymentMethod !== undefined) updatedItem.paymentMethod = item.paymentMethod;
            
            // Aktarım bilgilerini mutlaka koru
            if (item.transferredFrom !== undefined) updatedItem.transferredFrom = item.transferredFrom;
            // isTransferred alanını transferredFrom alanının varlığına göre hesapla
            updatedItem.isTransferred = item.transferredFrom ? true : false;
            
            // Düzenleme bilgilerini ayrı bir alan olarak ekle (Firebase'de saklanacak)
            updatedItem.editFlag = true;
            updatedItem.editDetails = editDetails;
            
            return updatedItem;
          }
          return item;
        });

        // Toplam bakiyeyi yeniden hesapla (tüm history'den)
        const newTotalAmount = updatedHistory.reduce((total, item) => {
          if (!item || item.amount === undefined) {
            console.warn('⚠️ Geçersiz item bulundu:', item);
            return total;
          }
          const itemAmount = parseFloat(item.amount) || 0;
          console.log(`📈 Bakiye hesaplama - ${item.type}: ${itemAmount}, Toplam: ${total + itemAmount}`);
          return total + itemAmount;
        }, 0);

        console.log('🎯 Yeni toplam bakiye:', newTotalAmount);

        // Firebase'e kaydet
        const currentTimeString = toIsoUtcString(new Date());
        transaction.update(debtRef, {
          amount: newTotalAmount,
          history: updatedHistory,
          lastUpdated: currentTimeString
        });
        
        console.log('💾 Firebase güncellendi');
      });

      closeEditTransactionModal();
      console.log('✅ İşlem düzenleme tamamlandı');
      
    } catch (error) {
      console.error('❌ İşlem düzenlenirken hata:', error);
      alert('İşlem düzenlenirken bir hata oluştu: ' + error.message);
    }
  };

  // İşlem silme fonksiyonları
  const openDeleteConfirmModal = (transaction, customerId) => {
    setTransactionToDelete({ ...transaction, customerId });
    setShowDeleteConfirmModal(true);
  };

  const closeDeleteConfirmModal = () => {
    setTransactionToDelete(null);
    setShowDeleteConfirmModal(false);
  };

  const showSuccessMessage = (transactionInfo) => {
    setDeletedTransactionInfo(transactionInfo);
    setShowSuccessPopup(true);
    
    // 3 saniye sonra otomatik kapat
    setTimeout(() => {
      setShowSuccessPopup(false);
      setDeletedTransactionInfo(null);
    }, 3000);
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    setDeletedTransactionInfo(null);
  };

  // Borç/Alacak ekleme başarı popup'ı fonksiyonları
  const showAddSuccessMessage = (transactionInfo) => {
    setAddedTransactionInfo(transactionInfo);
    setShowAddSuccessPopup(true);
    // 3 saniye sonra otomatik kapat
    setTimeout(() => {
      setShowAddSuccessPopup(false);
      setAddedTransactionInfo(null);
    }, 3000);
  };

  const closeAddSuccessPopup = () => {
    setShowAddSuccessPopup(false);
    setAddedTransactionInfo(null);
  };

  // Cari bazlı filtre fonksiyonları
  const updateCariFilter = (cariId, filterType, value) => {
    setCariFilters(prev => ({
      ...prev,
      [cariId]: {
        ...prev[cariId],
        [filterType]: value
      }
    }));
  };

  const getCariFilter = (cariId, filterType) => {
    return cariFilters[cariId]?.[filterType] || null;
  };

  const clearCariFilters = (cariId) => {
    setCariFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[cariId];
      return newFilters;
    });
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      console.log('🗑️ İşlem siliniyor:', transactionToDelete);
      
      const db = getFirestore();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error('Kullanıcı oturumu bulunamadı');
        return;
      }

      const customerId = transactionToDelete.customerId;
      const debtRef = fbDoc(db, `users/${currentUser.email}/customerList/${customerId}/debt/current`);
      
      let newTotalAmount = 0; // Yeni bakiye için değişken
      let hasHiddenRecords = false; // Gizli kayıtların silinip silinmediğini takip et

      await runTransaction(db, async (transaction) => {
        const debtDoc = await transaction.get(debtRef);
        
        if (!debtDoc.exists()) {
          throw new Error('Borç belgesi bulunamadı');
        }

        const debtData = debtDoc.data();
        const history = debtData.history || [];
        
        console.log('📊 Mevcut history:', history);
        console.log('📊 Mevcut history detayları:');
        history.forEach((item, idx) => {
          console.log(`  ${idx}: ${item.description} - ${item.amount} TL (${item.type})`);
        });
        console.log('🎯 Silinecek işlem ID:', transactionToDelete.id);

        // Gerçek history index'ini kullan
        let transactionIndex = transactionToDelete.realHistoryIndex;
        
        // Eğer realHistoryIndex yoksa, ID'den index'i çıkar (fallback)
        if (transactionIndex === undefined || transactionIndex === -1) {
          const transactionIdParts = transactionToDelete.id.split('_');
          transactionIndex = parseInt(transactionIdParts[transactionIdParts.length - 1]);
        }
        
        console.log('📍 Silinecek işlem index:', transactionIndex);
        
        // Index geçerli mi kontrol et
        if (isNaN(transactionIndex) || transactionIndex < 0 || transactionIndex >= history.length) {
          console.error('❌ İşlem history\'de bulunamadı:', transactionToDelete);
          console.log('🔍 Aranan işlem detayları:', {
            id: transactionToDelete.id,
            realHistoryIndex: transactionToDelete.realHistoryIndex,
            date: transactionToDelete.date,
            description: transactionToDelete.description,
            amount: transactionToDelete.amount,
            borc: transactionToDelete.borc,
            alacak: transactionToDelete.alacak,
            customerId: transactionToDelete.customerId
          });
          console.log('📋 Mevcut history detayları:', history.map((item, idx) => ({
            index: idx,
            id: item.id,
            date: item.date,
            description: item.description,
            amount: item.amount,
            type: item.type
          })));
          
          throw new Error(`İşlem history'de bulunamadı. ID: ${transactionToDelete.id}, RealHistoryIndex: ${transactionToDelete.realHistoryIndex}, Tarih: ${transactionToDelete.date}, Tutar: ${transactionToDelete.amount}`);
        }

        // İşlemi history'den kaldır
        const updatedHistory = history.filter((item, index) => index !== transactionIndex);
        
        // Ayrıca, aynı puantaj koduna sahip gizli transfer kayıtlarını da sil
        const deletedTransaction = history[transactionIndex];
        let finalHistory = updatedHistory;
        let hasHiddenRecords = false;
        
        if (deletedTransaction && deletedTransaction.puantajCode) {
          console.log('🔍 Aynı puantaj koduna sahip gizli transfer kayıtları aranıyor:', deletedTransaction.puantajCode);
          
          finalHistory = updatedHistory.filter(item => {
            // Eğer bu item aynı puantaj koduna sahipse ve transferType'ı puantaj_ ile başlıyorsa sil
            if (item.puantajCode === deletedTransaction.puantajCode && 
                item.transferType && 
                item.transferType.startsWith('puantaj_')) {
              console.log('🗑️ Gizli transfer kaydı siliniyor:', item.description);
              hasHiddenRecords = true;
              return false;
            }
            return true;
          });
          
          if (hasHiddenRecords) {
            console.log('📊 Gizli transfer kayıtları silindikten sonra history uzunluğu:', finalHistory.length);
          }
        }
        
        console.log('📝 Güncellenmiş history uzunluğu:', finalHistory.length);

        // Toplam bakiyeyi yeniden hesapla (tüm history'den)
        console.log('📊 Silme sonrası history detayları:');
        finalHistory.forEach((item, idx) => {
          console.log(`  ${idx}: ${item.description} - ${item.amount} TL (${item.type})`);
        });
        
        const newTotalAmount = finalHistory.reduce((total, item) => {
          if (!item || item.amount === undefined) {
            console.warn('⚠️ Geçersiz item bulundu:', item);
            return total;
          }
          const itemAmount = parseFloat(item.amount) || 0;
          console.log(`📈 Bakiye hesaplama - ${item.type}: ${itemAmount} TL, Ara toplam: ${total + itemAmount} TL`);
          return total + itemAmount;
        }, 0);

        console.log('🎯 Yeni toplam bakiye:', newTotalAmount);
        console.log('📊 Eski bakiye:', debtData.amount);
        console.log('📊 Silinen işlem tutarı:', transactionToDelete.borc || transactionToDelete.alacak);
        


        // Firebase'e kaydet
        const currentTimeString = toIsoUtcString(new Date());
        transaction.update(debtRef, {
          amount: newTotalAmount, // Negatif olamaz
          history: finalHistory,
          lastUpdated: currentTimeString
        });
        
        if (hasHiddenRecords) {
          console.log('💾 Firebase güncellendi - işlem ve gizli transfer kayıtları silindi');
        } else {
          console.log('💾 Firebase güncellendi - işlem silindi');
        }
        
        // CustomerDebts state'ini manuel olarak güncelle
        setCustomerDebts(prev => ({
          ...prev,
          [customerId]: {
            ...prev[customerId],
            amount: newTotalAmount,
            history: finalHistory,
            lastUpdated: currentTimeString
          }
        }));
      });

      // Başarı mesajını göster
      const transactionInfo = {
        date: transactionToDelete.date,
        description: transactionToDelete.description,
        amount: transactionToDelete.borc || transactionToDelete.alacak,
        type: transactionToDelete.borc > 0 ? 'Borç' : 'Alacak',
        newBalance: newTotalAmount, // Yeni bakiyeyi de ekle
        hasHiddenRecords: hasHiddenRecords // Gizli kayıtların silinip silinmediğini belirt
      };
      
      console.log('✅ İşlem silme tamamlandı - Yeni bakiye:', newTotalAmount);

      closeDeleteConfirmModal();
      showSuccessMessage(transactionInfo);
      console.log('✅ İşlem silme tamamlandı');
      
      // Cari transactions'ı manuel olarak güncelle
      const selectedCari = selectedCariList.find(cari => cari.id === transactionToDelete.customerId);
      if (selectedCari) {
        console.log('🔄 Cari transactions manuel olarak güncelleniyor:', selectedCari['Müşteri Adı']);
        
        // Mevcut transactions'ı al ve silinen işlemi çıkar
        setCariTransactions(prev => {
          const currentTransactions = prev[selectedCari.id] || [];
          const updatedTransactions = currentTransactions.filter(t => t.id !== transactionToDelete.id);
          
          return {
            ...prev,
            [selectedCari.id]: updatedTransactions
          };
        });
      }
      
    } catch (error) {
      console.error('❌ İşlem silinirken hata:', error);
      alert('İşlem silinirken bir hata oluştu: ' + error.message);
    }
  };



  // Para formatı - HopsPlus mantığına uygun
  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) return '0,00 ₺';
    
    // Türkçe sayı formatı (binlik ayracı nokta, ondalık ayraç virgül)
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Pozitif para formatı (Borç/Alacak sütunları için)
  const formatPositiveCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined || amount === 0) return '';
    
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const formatDate = (date) => formatTrDateOnly(date);

  // Sıralama Fonksiyonu
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  // Sıralama İkonları için Yardımcı Fonksiyon
  const renderSortIcon = (column) => {
    if (orderBy !== column) return null;
    return order === 'asc' ? ' 🔼' : ' 🔽';
  };

  // İşlem tablosu sıralama fonksiyonları
  const handleTransactionSort = (column) => {
    const isAsc = transactionOrderBy === column && transactionOrder === 'asc';
    setTransactionOrder(isAsc ? 'desc' : 'asc');
    setTransactionOrderBy(column);
  };

  const renderTransactionSortIcon = (column) => {
    if (transactionOrderBy !== column) return null;
    return transactionOrder === 'asc' ? ' 🔼' : ' 🔽';
  };

  // Excel rapor oluşturma fonksiyonu
  const generateExcelReport = (selectedCari, withKdv = false) => {
    const transactions = cariTransactions[selectedCari.id] || [];
    
    // Filtreleme uygula
    const cariStartDate = getCariFilter(selectedCari.id, 'startDate');
    const cariEndDate = getCariFilter(selectedCari.id, 'endDate');
    
    let filteredTransactions = transactions.filter(transaction => {
      const transactionDate = parseFlexible(transaction.date);
      
      // Cari bazlı tarih filtresi
      let matchesDateRange = true;
      if (cariStartDate) {
        const startOfDay = new Date(cariStartDate);
        startOfDay.setHours(0, 0, 0, 0);
        if (transactionDate < startOfDay) matchesDateRange = false;
      }
      if (cariEndDate) {
        const endOfDay = new Date(cariEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (transactionDate > endOfDay) matchesDateRange = false;
      }
      
      return matchesDateRange;
    });

    // 🎯 DOĞRU SIRALAMA ALGORİTMASI - Aynı gün içinde borç önce, alacak sonra
    console.log('🔍 [EXCEL] Sıralama öncesi transaction sayısı:', filteredTransactions.length);
    
    filteredTransactions.sort((a, b) => {
      const dateA = parseFlexible(a.date) || new Date(0);
      const dateB = parseFlexible(b.date) || new Date(0);
      
      // Önce tarihe göre sırala (eskiden yeniye)
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Aynı gün içinde: borçlar önce, alacaklar sonra
      const aBorcMu = (a.borc || 0) > 0;
      const bBorcMu = (b.borc || 0) > 0;
      
      console.log(`🔄 [EXCEL] Aynı gün karşılaştırma: ${a.date} - A:${aBorcMu ? 'BORÇ' : 'ALACAK'} vs B:${bBorcMu ? 'BORÇ' : 'ALACAK'}`);
      
      if (aBorcMu && !bBorcMu) return -1; // A borç, B alacak -> A önce
      if (!aBorcMu && bBorcMu) return 1;  // A alacak, B borç -> B önce
      
      return 0; // İkisi de aynı tür
    });
    
    console.log('✅ [EXCEL] Final sıralama:', filteredTransactions.map(t => `${t.date} - ${(t.borc || 0) > 0 ? 'BORÇ' : 'ALACAK'}`));

    // Excel verilerini hazırla
    const excelData = [];
    let currentBalance = 0;

    filteredTransactions.forEach((transaction) => {
      // İşlem türünü belirle
      let transactionType = '';
      let movementType = '';
      
      if (transaction.type === 'increase') {
        transactionType = 'Puantaj';
        movementType = 'Borç';
      } else if (transaction.type === 'decrease') {
        transactionType = 'Ödeme';
        movementType = 'Alacak';
      } else if (transaction.type === 'transfer_in') {
        transactionType = 'Aktarım';
        movementType = 'Borç';
      } else if (transaction.type === 'transfer_out') {
        transactionType = 'Aktarım';
        movementType = 'Alacak';
      }

      const borc = Number(transaction.borc) || 0;
      const alacak = Number(transaction.alacak) || 0;
      // Bakiye hesapla (kümülatif)
      currentBalance += borc - alacak;

      // Sözleşme numarasını çıkar (format: makineKodu-sözleşmeNo, örn: mantou1-0012)
      let sozlesmeNo = '';
      const machineName = transaction.machineName || '';
      const puantajCode = transaction.puantajCode || '';
      
      if (machineName && puantajCode) {
        // Makine adı ve puantaj kodu varsa birleştir
        sozlesmeNo = `${machineName}-${puantajCode}`;
      } else if (transaction.description) {
        // Description'dan makineAdı-sözleşmeNo formatını çıkarmaya çalış
        const desc = transaction.description;
        // "MakineAdi-0012" formatını ara
        const fullMatch = desc.match(/([A-Za-z0-9]+)-(\d{4})/);
        if (fullMatch && fullMatch[0]) {
          sozlesmeNo = fullMatch[0];
        } else {
          // "Puantaj: XXX - MakineAdi" formatından çıkar
          const puantajMatch = desc.match(/Puantaj:\s*([^\s]+)\s*-\s*([^\s(]+)/);
          if (puantajMatch && puantajMatch[1] && puantajMatch[2]) {
            sozlesmeNo = `${puantajMatch[2]}-${puantajMatch[1]}`;
          }
        }
      }

      excelData.push({
        'Cari Kod': selectedCari.cariCode || '',
        'Tarih': formatDate(transaction.date),
        'Sözleşme No': sozlesmeNo,
        'İşlem Türü': transactionType,
        'Hareket Cinsi': movementType,
        'TL Borç': borc,
        'TL Alacak': alacak,
        'Bakiye': currentBalance
      });
    });

    // Toplam hesaplamalar (KDV hariç — KDV’li/KDV’siz aynı)
    const totalBorc = filteredTransactions.reduce((sum, transaction) => sum + (Number(transaction.borc) || 0), 0);
    const totalAlacak = filteredTransactions.reduce((sum, transaction) => sum + (Number(transaction.alacak) || 0), 0);
    const finalBalance = totalBorc - totalAlacak;

    // Toplam satırlarını ekle
    excelData.push({
      'Cari Kod': '',
      'Tarih': '',
      'Sözleşme No': '',
      'İşlem Türü': 'TOPLAM',
      'Hareket Cinsi': '',
      'TL Borç': totalBorc,
      'TL Alacak': totalAlacak,
      'Bakiye': finalBalance
    });

    if (withKdv) {
      excelData.push({
        'Cari Kod': '',
        'Tarih': '',
        'Sözleşme No': '',
        'İşlem Türü': 'KDV (%20)',
        'Hareket Cinsi': '',
        'TL Borç': '',
        'TL Alacak': '',
        'Bakiye': kdvTutariFromNetBakiye(finalBalance)
      });
      excelData.push({
        'Cari Kod': '',
        'Tarih': '',
        'Sözleşme No': '',
        'İşlem Türü': 'Genel Toplam (KDV Dahil)',
        'Hareket Cinsi': '',
        'TL Borç': '',
        'TL Alacak': '',
        'Bakiye': genelToplamKdvDahil(finalBalance)
      });
    }

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 15 }, // Cari Kod
      { wch: 12 }, // Tarih
      { wch: 18 }, // Sözleşme No
      { wch: 15 }, // İşlem Türü
      { wch: 15 }, // Hareket Cinsi
      { wch: 15 }, // TL Borç
      { wch: 15 }, // TL Alacak
      { wch: 15 }  // Bakiye
    ];
    ws['!cols'] = colWidths;

    // Para formatı için format kodu (Türk Lirası - nokta ayraçlı)
    const currencyFormat = '#,##0.00_-';
    
    // Tüm hücreler için font ve stil ayarları
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({c: C, r: R});
        if (!ws[cellAddress]) continue;
        
        // xlsx-js-style formatında stil objesi oluştur
        ws[cellAddress].s = {
          font: {
            name: 'Segoe UI',
            sz: 8,
            color: { rgb: "000000" },
            bold: R === 0 // İlk satır (başlık) kalın olsun
          },
          alignment: {
            vertical: 'center',
            horizontal: 'center'
          }
        };
        
        // Para sütunları için (TL Borç, TL Alacak, Bakiye) özel format
        if (C === 5 || C === 6 || C === 7) { // 5: TL Borç, 6: TL Alacak, 7: Bakiye
          ws[cellAddress].s.numFmt = currencyFormat;
        }
        
        // Başlık satırı için ek stil
        if (R === 0) {
          ws[cellAddress].s.fill = {
            patternType: 'solid',
            fgColor: { rgb: "E6E6FA" }
          };
          ws[cellAddress].s.border = {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          };
        }
      }
    }

    // Worksheet'i workbook'a ekle
    XLSX.utils.book_append_sheet(wb, ws, 'Cari Raporu');

    // Dosya adını oluştur
    const kdvSuffix = withKdv ? '_KDV20' : '';
    const fileName = `${selectedCari['Müşteri Adı']}_Cari_Raporu_${toIsoUtcString(new Date()).slice(0, 10)}${kdvSuffix}.xlsx`;

    // Excel dosyasını indir
    XLSX.writeFile(wb, fileName);
  };

  // Sadece Borçları Gösteren Excel rapor oluşturma fonksiyonu
  const generateExcelReportDebtOnly = (selectedCari, withKdv = false) => {
    const transactions = cariTransactions[selectedCari.id] || [];
    
    // Filtreleme uygula
    const cariStartDate = getCariFilter(selectedCari.id, 'startDate');
    const cariEndDate = getCariFilter(selectedCari.id, 'endDate');
    
    let filteredTransactions = transactions.filter(transaction => {
      const transactionDate = parseFlexible(transaction.date);
      
      // Cari bazlı tarih filtresi
      let matchesDateRange = true;
      if (cariStartDate) {
        const startOfDay = new Date(cariStartDate);
        startOfDay.setHours(0, 0, 0, 0);
        if (transactionDate < startOfDay) matchesDateRange = false;
      }
      if (cariEndDate) {
        const endOfDay = new Date(cariEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (transactionDate > endOfDay) matchesDateRange = false;
      }
      
      return matchesDateRange;
    });

    // Sadece borç işlemlerini filtrele (alacakları hariç tut)
    filteredTransactions = filteredTransactions.filter(transaction => {
      return (transaction.borc || 0) > 0;
    });

    // Tarihe göre sırala (eskiden yeniye)
    filteredTransactions.sort((a, b) => {
      const dateA = parseFlexible(a.date) || new Date(0);
      const dateB = parseFlexible(b.date) || new Date(0);
      return dateA - dateB;
    });

    // Excel verilerini hazırla
    const excelData = [];
    let currentBalance = 0;

    filteredTransactions.forEach((transaction) => {
      // İşlem türünü belirle
      let transactionType = '';
      
      if (transaction.type === 'increase') {
        transactionType = 'Puantaj';
      } else if (transaction.type === 'transfer_in') {
        transactionType = 'Aktarım';
      }

      const borc = Number(transaction.borc) || 0;
      // Bakiye hesapla (kümülatif - sadece borçlar)
      currentBalance += borc;

      // Sözleşme numarasını çıkar
      let sozlesmeNo = '';
      const machineName = transaction.machineName || '';
      const puantajCode = transaction.puantajCode || '';
      
      if (machineName && puantajCode) {
        sozlesmeNo = `${machineName}-${puantajCode}`;
      } else if (transaction.description) {
        const desc = transaction.description;
        const fullMatch = desc.match(/([A-Za-z0-9]+)-(\d{4})/);
        if (fullMatch && fullMatch[0]) {
          sozlesmeNo = fullMatch[0];
        } else {
          const puantajMatch = desc.match(/Puantaj:\s*([^\s]+)\s*-\s*([^\s(]+)/);
          if (puantajMatch && puantajMatch[1] && puantajMatch[2]) {
            sozlesmeNo = `${puantajMatch[2]}-${puantajMatch[1]}`;
          }
        }
      }

      excelData.push({
        'Cari Kod': selectedCari.cariCode || '',
        'Tarih': formatDate(transaction.date),
        'Sözleşme No': sozlesmeNo,
        'İşlem Türü': transactionType,
        'TL Borç': borc,
        'Bakiye': currentBalance
      });
    });

    // Toplam hesaplamalar
    const totalBorc = filteredTransactions.reduce((sum, transaction) => sum + (Number(transaction.borc) || 0), 0);

    // Toplam satırlarını ekle
    excelData.push({
      'Cari Kod': '',
      'Tarih': '',
      'Sözleşme No': '',
      'İşlem Türü': 'TOPLAM',
      'TL Borç': totalBorc,
      'Bakiye': totalBorc
    });

    if (withKdv) {
      excelData.push({
        'Cari Kod': '',
        'Tarih': '',
        'Sözleşme No': '',
        'İşlem Türü': 'KDV (%20)',
        'TL Borç': '',
        'Bakiye': kdvTutariFromNetBakiye(totalBorc)
      });
      excelData.push({
        'Cari Kod': '',
        'Tarih': '',
        'Sözleşme No': '',
        'İşlem Türü': 'Genel Toplam (KDV Dahil)',
        'TL Borç': '',
        'Bakiye': genelToplamKdvDahil(totalBorc)
      });
    }

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 15 }, // Cari Kod
      { wch: 12 }, // Tarih
      { wch: 18 }, // Sözleşme No
      { wch: 15 }, // İşlem Türü
      { wch: 15 }, // TL Borç
      { wch: 15 }  // Bakiye
    ];
    ws['!cols'] = colWidths;

    // Para formatı için format kodu
    const currencyFormat = '#,##0.00_-';
    
    // Tüm hücreler için font ve stil ayarları
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({c: C, r: R});
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: {
            name: 'Segoe UI',
            sz: 8,
            color: { rgb: "000000" },
            bold: R === 0
          },
          alignment: {
            vertical: 'center',
            horizontal: 'center'
          }
        };
        
        // Para sütunları için özel format (TL Borç, Bakiye)
        if (C === 4 || C === 5) {
          ws[cellAddress].s.numFmt = currencyFormat;
        }
        
        // Başlık satırı için ek stil
        if (R === 0) {
          ws[cellAddress].s.fill = {
            patternType: 'solid',
            fgColor: { rgb: "E6E6FA" }
          };
          ws[cellAddress].s.border = {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          };
        }
      }
    }

    // Worksheet'i workbook'a ekle
    XLSX.utils.book_append_sheet(wb, ws, 'Hakediş Raporu');

    // Dosya adını oluştur
    const kdvSuffix = withKdv ? '_KDV20' : '';
    const fileName = `${selectedCari['Müşteri Adı']}_Hakedis_Raporu_${toIsoUtcString(new Date()).slice(0, 10)}${kdvSuffix}.xlsx`;

    // Excel dosyasını indir
    XLSX.writeFile(wb, fileName);
  };

  // Gelişmiş Türkçe karakter dönüştürme fonksiyonu - Büyük harfler dahil
  const convertTurkishChars = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    const charMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G', 
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U',
      // Ekstra özel karakterler
      'â': 'a', 'Â': 'A',
      'î': 'i', 'Î': 'I',
      'û': 'u', 'Û': 'U'
    };
    
    // Önce güvenli dönüştürme yap
    let convertedText = text.replace(/[çÇğĞıİöÖşŞüÜâÂîÎûÛ]/g, (match) => charMap[match] || match);
    
    console.log(`🔤 Karakter dönüştürme: "${text}" -> "${convertedText}"`);
    
    return convertedText;
  };

  // PDF rapor oluşturma fonksiyonu
const generatePDFReport = async (selectedCari, withKdv = false) => {
    const transactions = cariTransactions[selectedCari.id] || [];
    
    // Filtreleme uygula
    const cariStartDate = getCariFilter(selectedCari.id, 'startDate');
    const cariEndDate = getCariFilter(selectedCari.id, 'endDate');
    
    let filteredTransactions = transactions.filter(transaction => {
      const transactionDate = parseFlexible(transaction.date);
      
      // Cari bazlı tarih filtresi
      let matchesDateRange = true;
      if (cariStartDate) {
        const startOfDay = new Date(cariStartDate);
        startOfDay.setHours(0, 0, 0, 0);
        if (transactionDate < startOfDay) matchesDateRange = false;
      }
      if (cariEndDate) {
        const endOfDay = new Date(cariEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (transactionDate > endOfDay) matchesDateRange = false;
      }
      
      return matchesDateRange;
    });

    // 🎯 DOĞRU SIRALAMA ALGORİTMASI - Aynı gün içinde borç önce, alacak sonra
    console.log('🔍 [PDF] Sıralama öncesi transaction sayısı:', filteredTransactions.length);
    
    filteredTransactions.sort((a, b) => {
      const dateA = parseFlexible(a.date) || new Date(0);
      const dateB = parseFlexible(b.date) || new Date(0);
      
      // Önce tarihe göre sırala (eskiden yeniye)
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Aynı gün içinde: borçlar önce, alacaklar sonra
      const aBorcMu = (a.borc || 0) > 0;
      const bBorcMu = (b.borc || 0) > 0;
      
      console.log(`🔄 [PDF] Aynı gün karşılaştırma: ${a.date} - A:${aBorcMu ? 'BORÇ' : 'ALACAK'} vs B:${bBorcMu ? 'BORÇ' : 'ALACAK'}`);
      
      if (aBorcMu && !bBorcMu) return -1; // A borç, B alacak -> A önce
      if (!aBorcMu && bBorcMu) return 1;  // A alacak, B borç -> B önce
      
      return 0; // İkisi de aynı tür
    });
    
    console.log('✅ [PDF] Final sıralama:', filteredTransactions.map(t => `${t.date} - ${(t.borc || 0) > 0 ? 'BORÇ' : 'ALACAK'}`));

    // PDF verilerini hazırla
    const pdfData = [];
    let currentBalance = 0;

    filteredTransactions.forEach((transaction) => {
      // İşlem türünü belirle
      let transactionType = '';
      let movementType = '';
      
      if (transaction.type === 'increase') {
        transactionType = 'Puantaj';
        movementType = 'Borç';
      } else if (transaction.type === 'decrease') {
        transactionType = 'Ödeme';
        movementType = 'Alacak';
      } else if (transaction.type === 'transfer_in') {
        transactionType = 'Aktarım';
        movementType = 'Borç';
      } else if (transaction.type === 'transfer_out') {
        transactionType = 'Aktarım';
        movementType = 'Alacak';
      }

      const borc = Number(transaction.borc) || 0;
      const alacak = Number(transaction.alacak) || 0;
      // Bakiye hesapla (kümülatif)
      currentBalance += borc - alacak;

      // Sözleşme numarasını çıkar (format: makineKodu-sözleşmeNo, örn: mantou1-0012)
      let sozlesmeNo = '';
      const machineName = transaction.machineName || '';
      const puantajCode = transaction.puantajCode || '';
      
      if (machineName && puantajCode) {
        // Makine adı ve puantaj kodu varsa birleştir
        sozlesmeNo = `${machineName}-${puantajCode}`;
      } else if (transaction.description) {
        // Description'dan makineAdı-sözleşmeNo formatını çıkarmaya çalış
        const desc = transaction.description;
        // "MakineAdi-0012" formatını ara
        const fullMatch = desc.match(/([A-Za-z0-9]+)-(\d{4})/);
        if (fullMatch && fullMatch[0]) {
          sozlesmeNo = fullMatch[0];
        } else {
          // "Puantaj: XXX - MakineAdi" formatından çıkar
          const puantajMatch = desc.match(/Puantaj:\s*([^\s]+)\s*-\s*([^\s(]+)/);
          if (puantajMatch && puantajMatch[1] && puantajMatch[2]) {
            sozlesmeNo = `${puantajMatch[2]}-${puantajMatch[1]}`;
          }
        }
      }

      pdfData.push([
        selectedCari.cariCode || '',
        formatDate(transaction.date),
        convertTurkishChars(sozlesmeNo),
        convertTurkishChars(transactionType),
        convertTurkishChars(movementType),
        borc.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
        alacak.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
        currentBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })
      ]);
    });

    // Toplam hesaplamalar (işlem satırlarıyla aynı — KDV hariç)
    const totalBorc = filteredTransactions.reduce((sum, transaction) => sum + (Number(transaction.borc) || 0), 0);
    const totalAlacak = filteredTransactions.reduce((sum, transaction) => sum + (Number(transaction.alacak) || 0), 0);
    const finalBalance = totalBorc - totalAlacak;

    // Toplam satırını PDF verilerine ekle (Cari Kod, Tarih, Sözleşme No, İşlem Türü, Hareket Cinsi, TL Borç, TL Alacak, Bakiye)
    pdfData.push([
      '',      // Cari Kod
      '',      // Tarih
      '',      // Sözleşme No
      'TOPLAM', // İşlem Türü
      '',      // Hareket Cinsi
      totalBorc.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
      totalAlacak.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
      finalBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })
    ]);

    if (withKdv) {
      const kdvTutar = kdvTutariFromNetBakiye(finalBalance);
      const genelToplam = genelToplamKdvDahil(finalBalance);
      pdfData.push([
        '', '', '', convertTurkishChars('KDV (%20)'), '', '', '', kdvTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })
      ]);
      pdfData.push([
        '', '', '', convertTurkishChars('Genel Toplam (KDV Dahil)'), '', '', '', genelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })
      ]);
    }

    // PDF oluştur
    const doc = new jsPDF();
    
    // Türkçe karakter desteği için font ayarı
    doc.setFont('helvetica');
    
    // Text wrapping fonksiyonu
    const addWrappedText = (text, x, startY, maxWidth, lineHeight = 6) => {
      const words = text.split(' ');
      let line = '';
      let y = startY;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const testWidth = doc.getTextWidth(testLine);
        
        if (testWidth > maxWidth && n > 0) {
          doc.text(line.trim(), x, y);
          line = words[n] + ' ';
          y += lineHeight;
        } else {
          line = testLine;
        }
      }
      doc.text(line.trim(), x, y);
      return y + lineHeight; // Son satırın y pozisyonunu döndür
    };
    
    // 🏢 RAPOR SAHİBİ FİRMA BİLGİSİ - SOL ÜST TARAFA EKLEMESİ
    let currentY = 15;
    try {
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.email) {
        // Kullanıcı bilgilerini Firebase'den al
        const db = getFirestore();
        const userDocRef = fbDoc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const companyName = userData.companyName || userData.company || 'Bilinmeyen Firma';
          
          // Rapor sahibi firma bilgisini sol üst tarafa ekle
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const companyText = `Rapor sahibi firma: ${convertTurkishChars(companyName)}`;
          doc.text(companyText, 20, currentY);
          currentY += 12;
        }
      }
    } catch (error) {
      console.error('❌ Kullanıcı bilgileri alınamadı:', error);
      currentY += 5; // Hata durumunda da biraz boşluk bırak
    }
    
    // Başlık (Türkçe karakterler güvenli) - Text wrapping ile
    doc.setFontSize(16);
    const customerName = convertTurkishChars(selectedCari['Müşteri Adı']);
    const titleText = `${customerName} - Cari Raporu`;
    currentY = addWrappedText(titleText, 20, currentY, 170, 8); // Max 170px genişlik
    
    // Tarih
    doc.setFontSize(12);
    currentY += 5; // Başlık ile tarih arası boşluk
    doc.text(`Rapor Tarihi: ${formatTrDateOnly(new Date())}`, 20, currentY);
    currentY += 7;
    doc.setFontSize(10);
    doc.text(
      withKdv
        ? 'Islem satirlari KDV haric; altta KDV ozeti (net bakiye uzerinden %20).'
        : 'Tutarlar: KDV haric.',
      20,
      currentY
    );
    currentY += 7;
    doc.setFontSize(12);
    
    // Cari kod
    if (selectedCari.cariCode) {
      doc.text(`Cari Kod: ${convertTurkishChars(selectedCari.cariCode || '')}`, 20, currentY);
      currentY += 10;
    } else {
      currentY += 5;
    }

    // Tablo (Türkçe karakterler dönüştürülmüş)
    autoTable(doc, {
      head: [['Cari Kod', 'Tarih', 'Sozlesme No', 'Islem Turu', 'Hareket Cinsi', 'TL Borc', 'TL Alacak', 'Bakiye']],
      body: pdfData,
      startY: currentY, // Dinamik başlangıç pozisyonu
      styles: {
        fontSize: 7,
        cellPadding: 2,
        font: 'helvetica'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'left'  // Başlık satırları sola yaslanacak
      },
      columnStyles: {
        0: { halign: 'left' },   // Cari Kod
        1: { halign: 'left' },   // Tarih
        2: { halign: 'left' },   // Sözleşme No
        3: { halign: 'left' },   // İşlem Türü
        4: { halign: 'left' },   // Hareket Cinsi
        5: { halign: 'left' },   // TL Borç
        6: { halign: 'left' },   // TL Alacak
        7: { halign: 'left' }    // Bakiye
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      didParseCell: function (data) {
        if (data.section === 'body') {
          const summaryRows = withKdv ? 3 : 1;
          if (data.row.index >= pdfData.length - summaryRows) {
            data.cell.styles.fillColor = [220, 220, 220];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = [0, 0, 0];
          }
        }
      }
    });



    // 📄 FOOTER EKLEMESİ
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Her sayfa için footer ekle
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer metni
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      
      const footerText = 'Bu belge HOPS tarafindan dijital olarak olusturulmustur.';
      const textWidth = doc.getTextWidth(footerText);
      const xPosition = (pageWidth - textWidth) / 2; // Ortala
      
      // Sayfanın alt kısmına yerleştir
      doc.text(footerText, xPosition, pageHeight - 10);
      
      // Sayfa numarası (sağ alt köşe)
      doc.setFont('helvetica', 'normal');
      doc.text(`Sayfa ${i}/${pageCount}`, pageWidth - 30, pageHeight - 10);
    }

    // Dosya adını oluştur ve indir
    const kdvSuffix = withKdv ? '_KDV20' : '';
    const fileName = `${convertTurkishChars(selectedCari['Müşteri Adı'])}_Cari_Raporu_${toIsoUtcString(new Date()).slice(0, 10)}${kdvSuffix}.pdf`;
    doc.save(fileName);
  };

  // ---------------------------------------------------------------
  // Yardımcı: Borç-öncelikli tarih karşılaştırıcısı
  // ---------------------------------------------------------------
  const compareTransactions = (a, b, desc = false) => {
    // Sadece gün bazında karşılaştırma yap (saat farklarını göz ardı et)
    const dateStrA = (a.date || '').toString().slice(0, 10); // 'YYYY-MM-DD'
    const dateStrB = (b.date || '').toString().slice(0, 10);

    if (dateStrA !== dateStrB) {
      // Farklı günler => tarihe göre sırala
      const diff = (parseFlexible(dateStrA) || new Date(0)) - (parseFlexible(dateStrB) || new Date(0));
      return desc ? -diff : diff;
    }

    // Aynı gün: önce borçlar, sonra alacaklar
    const isDebtA = (a.borc || 0) > 0;
    const isDebtB = (b.borc || 0) > 0;
    if (isDebtA !== isDebtB) {
      return isDebtB ? 1 : -1; // Borç önce gelsin (A borç ise önce gelir)
    }

    // İkisi de aynı türdeyse orijinal saat/timestamp sırasını koru
    const timeDiff = (parseFlexible(a.date) || new Date(0)) - (parseFlexible(b.date) || new Date(0));
    return desc ? -timeDiff : timeDiff;
  };

  if (loading) {
    return (
      <div className="bank-card-loading">
        <div className="loading-spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bank-card-container">
      <div className="bank-card-header">
        <h2>
          <CreditCard className="header-icon" />
          Cari Kasa
        </h2>
      </div>

      {/* Cari Seçimi */}
      <div className="cari-selection-section">
        <h3>Cari Seçimi</h3>
        
        {/* Filtre ve Arama Bölümü */}
        <div className="unified-filter-container">
          <div className="filter-content">
            <div className="filter-item customer-search">
              <label>
                <Search size={16} />
                Müşteri Arama:
              </label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Müşteri adı veya cari kodu ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(toTurkishUpperCase(e.target.value))}
                  className="unified-search-input"
                />
              </div>
            </div>

            <div className="filter-item">
              <label>
                <Calendar size={16} />
                Son İşlem Tarihi:
              </label>
              <Select
                options={lastTransactionDaysOptions}
                value={selectedLastTransactionDays}
                onChange={setSelectedLastTransactionDays}
                placeholder="Son işlem tarihini seçin"
                className="react-select"
                classNamePrefix="select"
                isClearable={true}
              />
            </div>

            <div className="filter-item">
              <label>
                <Filter size={16} />
                Bakiye Türü:
              </label>
              <Select
                options={balanceTypeOptions}
                value={selectedBalanceType}
                onChange={setSelectedBalanceType}
                placeholder="Bakiye türünü seçin"
                className="react-select"
                classNamePrefix="select"
                isClearable={true}
              />
            </div>
          </div>
        </div>
        
        {/* Cari Listesi Açıklayıcı Yazı - Puantajlar formatında */}
        {sortedCariList.length > 0 && (
          <div className="pagination-info">
            <p>
              Toplam {cariList.length} cari müşteriden {sortedCariList.length} adet cari listeleniyor
            </p>
          </div>
        )}
        
        <div className="cari-list-container">
          <table className="cari-list-table">
            <thead ref={tableHeaderRef}>
              <tr>
                <th onClick={() => handleSort('Müşteri Adı')} style={{ cursor: 'pointer' }}>
                  Müşteri Adı {renderSortIcon('Müşteri Adı')}
                </th>
                <th onClick={() => handleSort('cariCode')} style={{ cursor: 'pointer' }}>
                  Cari Kod {renderSortIcon('cariCode')}
                </th>
                <th onClick={() => handleSort('Son İşlem')} style={{ cursor: 'pointer' }}>
                  Son İşlem {renderSortIcon('Son İşlem')}
                </th>
                <th onClick={() => handleSort('Onay')} style={{ cursor: 'pointer' }}>
                  Durum {renderSortIcon('Onay')}
                </th>
                <th onClick={() => handleSort('Bakiye')} style={{ cursor: 'pointer' }}>
                  Bakiye {renderSortIcon('Bakiye')}
                </th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {sortedCariList.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-results">
                    {searchTerm ? 'Arama kriterlerine uygun müşteri bulunamadı.' : 'Müşteri bulunamadı.'}
                  </td>
                </tr>
              ) : (
                sortedCariList.map((cari, index) => {
                  const isSelected = selectedCariList.some(selected => selected.id === cari.id);
                  
                  // Seçili müşterilerin sırasını hesapla (header'ın altında sırayla dizilmesi için)
                  let selectionIndex = -1;
                  if (isSelected) {
                    // Bu müşterinin seçili listesindeki sırasını bul
                    const selectedIndices = sortedCariList
                      .slice(0, index + 1) // Bu satıra kadar olan satırları kontrol et
                      .filter(c => selectedCariList.some(selected => selected.id === c.id))
                      .length - 1; // 0'dan başlayan index
                    selectionIndex = selectedIndices;
                  }
                  
                  const topPosition = isSelected ? 56 + (selectionIndex * 58) : 'auto';
                  
                  return (
                    <tr
                      key={cari.id}
                      className={`cari-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleCariSelect(cari)}
                      style={isSelected ? { top: `${topPosition}px` } : {}}
                    >
                      <td className="customer-name">
                        <User className="cari-icon" size={16} />
                        {cari['Müşteri Adı'] || cari.Name || 'Belirtilmemiş'}
                      </td>
                      <td className="cari-code">
                        {cari.cariCode || 'Belirtilmemiş'}
                      </td>
                      <td className="last-transaction-date">
                        {customerDebts[cari.id]?.lastTransactionDate 
                          ? formatDate(customerDebts[cari.id].lastTransactionDate) 
                          : 'İşlem Yok'}
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${cari['Onay'] === CUSTOMER_STATUS.APPROVED ? 'approved' : 'pending'}`}>
                          {cari['Onay'] === CUSTOMER_STATUS.APPROVED ? 'Onaylandı' : 'Onay Bekliyor'}
                        </span>
                      </td>
                      <td className={`balance amount-cell ${(() => {
                          const transactions = cariTransactions[cari.id] || [];
                          const calcAmount = calculateBalance(transactions);
                          const dbAmount = typeof customerDebts[cari.id]?.amount === 'number' ? customerDebts[cari.id].amount : 0;
                          const finalAmount = calcAmount !== dbAmount ? calcAmount : dbAmount;
                          return finalAmount >= 0 ? 'borc' : 'alacak';
                        })()}`}>
                        {customerDebts[cari.id]?.loading ? (
                          <span className="loading-balance">Yükleniyor...</span>
                        ) : (
                          (() => {
                            const transactions = cariTransactions[cari.id] || [];
                            const calcAmount = calculateBalance(transactions);
                            const dbAmount = typeof customerDebts[cari.id]?.amount === 'number' ? customerDebts[cari.id].amount : 0;
                            const finalAmount = dbAmount !== 0 ? dbAmount : calcAmount;
                            return formatCurrency(finalAmount);
                          })()
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              {/* Filtrelenmiş Toplam Bakiye - sadece filtre uygulandığında göster */}
              {hasActiveFilters() && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold', padding: '10px' }}>
                    Kasanın Filtrelenmiş Toplam Bakiyesi:
                  </td>
                  <td
                    className={`amount-cell ${calculateFilteredTotalCariBalance() >= 0 ? 'borc' : 'alacak'}`}
                    style={{ fontWeight: 'bold', padding: '10px' }}
                  >
                    {formatCurrency(calculateFilteredTotalCariBalance())}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold', padding: '10px' }}>
                  Kasanın Toplam Bakiyesi:
                </td>
                <td
                  className={`amount-cell ${calculateTotalCariBalance() >= 0 ? 'borc' : 'alacak'}`}
                  style={{ fontWeight: 'bold', padding: '10px' }}
                >
                  {formatCurrency(calculateTotalCariBalance())}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* İşlem Geçmişi - Çoklu Cari */}
      {Array.isArray(selectedCariList) && selectedCariList.length > 0 && (
        <div className="multiple-cari-sections">
          {selectedCariList.map(selectedCari => {
            const transactions = cariTransactions[selectedCari.id] || [];
            
            // Önce filtreleme uygula (cari bazlı filtreler)
            const cariStartDate = getCariFilter(selectedCari.id, 'startDate');
            const cariEndDate = getCariFilter(selectedCari.id, 'endDate');
            
            let filteredTransactions = transactions.filter(transaction => {
              const transactionDate = parseFlexible(transaction.date);
              
              // Cari bazlı tarih filtresi (dahil)
              let matchesDateRange = true;
              if (cariStartDate) {
                const startOfDay = new Date(cariStartDate);
                startOfDay.setHours(0, 0, 0, 0);
                if (transactionDate < startOfDay) matchesDateRange = false;
              }
              if (cariEndDate) {
                const endOfDay = new Date(cariEndDate);
                endOfDay.setHours(23, 59, 59, 999);
                if (transactionDate > endOfDay) matchesDateRange = false;
              }
              
              return matchesDateRange;
            });
            
            // Özel sıralama: İlk girişten son girişe doğru (en eski en üstte) ve aynı gün içinde alacaklar en altta
            console.log('🔍 Sıralama öncesi işlemler:', filteredTransactions.map(t => `${t.date} - ${t.borc > 0 ? 'BORÇ' : 'ALACAK'} - ${t.description}`));
            
            filteredTransactions = [...filteredTransactions].sort(compareTransactions);
            
            console.log('🔍 Sıralama sonrası işlemler:', filteredTransactions.map(t => `${t.date} - ${t.borc > 0 ? 'BORÇ' : 'ALACAK'} - ${t.description}`));
            
            // Bakiye hesaplaması için eskiden yeniye sıralanmış versiyon lazım
            const sortedForBalance = [...filteredTransactions].sort((a, b) => (parseFlexible(a.date) || new Date(0)) - (parseFlexible(b.date) || new Date(0)));
            
            return (
              <div key={selectedCari.id} className="selected-cari-section">
                <button
                  className="cari-remove-btn"
                  onClick={() => handleCariRemove(selectedCari)}
                  title="Bu cariyi seçimden kaldır"
                >
                  <X size={18} stroke="#3b82f6" strokeWidth={2.5} />
                </button>
                <div className="transaction-table-section">
                  <div className="transaction-header">
                    {/* Sol: Müşteri Adı */}
                    <div className="transaction-header-left">
                      <h4>Cari işlem geçmişi sayfası: {selectedCari['Müşteri Adı'] || selectedCari.Name}</h4>
                    </div>
                    
                    {/* Orta: Borç/Alacak Butonları */}
                    <div className="transaction-buttons">
                      <button 
                        className="add-credit-btn"
                        onClick={() => openAddCreditModal(selectedCari)}
                      >
                        <Plus size={16} /> Alacak Ekle
                      </button>
                      <button 
                        className="add-transaction-btn"
                        onClick={() => openAddTransactionModal(selectedCari)}
                      >
                        <Plus size={16} /> Borç Ekle
                      </button>
                    </div>
                    
                    {/* Sağ: Tarih Filtresi */}
                    <div className="cari-date-filters">
                      <div className="date-filters-row">
                        <div className="date-filter-group">
                          <label>Başlangıç Tarihi</label>
                          <DatePicker
                            selected={getCariFilter(selectedCari.id, 'startDate')}
                            onChange={(date) => updateCariFilter(selectedCari.id, 'startDate', date)}
                            dateFormat="dd.MM.yyyy"
                            placeholderText="Başlangıç tarihi"
                            className="date-picker-input"
                            isClearable={true}
                            locale="tr"
                          />
                        </div>
                        <div className="date-filter-group">
                          <label>Bitiş Tarihi</label>
                          <DatePicker
                            selected={getCariFilter(selectedCari.id, 'endDate')}
                            onChange={(date) => updateCariFilter(selectedCari.id, 'endDate', date)}
                            dateFormat="dd.MM.yyyy"
                            placeholderText="Bitiş tarihi"
                            className="date-picker-input"
                            isClearable={true}
                            locale="tr"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* İşlem Tablosu */}
                  <div className="excel-table-container">
                    <table className="excel-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleTransactionSort('date')} style={{ cursor: 'pointer' }}>
                            Tarih {renderTransactionSortIcon('date')}
                          </th>
                          <th onClick={() => handleTransactionSort('type')} style={{ cursor: 'pointer' }}>
                            İşlem Türü {renderTransactionSortIcon('type')}
                          </th>
                          <th onClick={() => handleTransactionSort('description')} style={{ cursor: 'pointer' }}>
                            Açıklama {renderTransactionSortIcon('description')}
                          </th>
                          <th onClick={() => handleTransactionSort('borc')} style={{ cursor: 'pointer' }}>
                            Borç {renderTransactionSortIcon('borc')}
                          </th>
                          <th onClick={() => handleTransactionSort('alacak')} style={{ cursor: 'pointer' }}>
                            Alacak {renderTransactionSortIcon('alacak')}
                          </th>
                          <th>Bakiye</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                    <tbody>
                      {filteredTransactions.map((transaction, index) => (
                        <tr 
                          key={transaction.id} 
                          className={`transaction-row ${getTransactionRowClass(transaction.type)}`}
                        >
                          <td>{formatDate(transaction.date)}</td>
                          <td>
                            {/* İşlem türü dinamik olarak gösterilecek */}
                            <div className="transaction-flags-container">
                              {/* Ana işlem türü */}
                              <span className={`transaction-type ${transaction.type === 'increase' ? 'puantaj' : transaction.type === 'decrease' ? 'payment' : transaction.type === 'transfer_in' ? 'transfer-in' : transaction.type === 'transfer_out' ? 'transfer-out' : ''}`}> 
                                {getTransactionTypeLabel(transaction.type)}
                              </span>
                              
                              {/* Aktarım flag'i */}
                              {transaction.isTransferred && (
                                <span
                                  className="transaction-type transfer-in"
                                  style={{ marginLeft: '4px' }}
                                  title={`${transaction.transferOrigin} müşterisinden aktarıldı`}
                                >
                                  Aktarım
                                </span>
                              )}
                              
                              {/* Düzenleme flag'i */}
                              {(transaction.isEdited || transaction.editFlag) && (
                                <span
                                  className="transaction-type edit-flag"
                                  style={{ marginLeft: '4px', background: '#888', color: 'white' }}
                                  title={`Düzenleme Detayı:
Orijinal Tutar: ${transaction.originalAmount ? formatCurrency(Math.abs(transaction.originalAmount)) : 'N/A'}
Güncel Tutar: ${formatCurrency(Math.abs(transaction.borc || transaction.alacak || 0))}
Son Güncelleme: ${transaction.updatedAt ? formatTrDateTime(transaction.updatedAt) : 'N/A'}`}
                                >
                                  DÜZENLENDİ
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{transaction.description}</td>
                          <td className={`amount-cell ${transaction.borc > 0 ? 'borc' : ''}`}>
                            {formatPositiveCurrency(transaction.borc)}
                          </td>
                          <td className={`amount-cell ${transaction.alacak > 0 ? 'alacak' : ''}`}>
                            {formatPositiveCurrency(transaction.alacak)}
                          </td>
                          <td className={`amount-cell ${(() => {
                            const balance = calculateBalanceForTransaction(sortedForBalance, transaction);
                            return balance >= 0 ? 'borc' : 'alacak';
                          })()}`}>
                            {formatCurrency(calculateBalanceForTransaction(sortedForBalance, transaction))}
                          </td>
                          <td>
                            <div className="transaction-actions">
                              <button 
                                className="edit-button"
                                onClick={() => {
                                  // Hangi sütunda değer var ona göre işlem tipini belirle
                                  const transactionType = transaction.borc > 0 ? 'Borç' : 'Alacak';
                                  const amount = transaction.borc > 0 ? transaction.borc : transaction.alacak;
                                  openEditTransactionModal({
                                    ...transaction, 
                                    customerId: selectedCari.id,
                                    amount: amount
                                  }, transactionType);
                                }}
                                title="İşlemi Düzenle"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button 
                                className="delete-button"
                                onClick={() => openDeleteConfirmModal(transaction, selectedCari.id)}
                                title="İşlemi Sil"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                      <tfoot>
                        {/* Filtrelenmiş Bakiye - sadece tarih filtresi uygulandığında göster */}
                        {(cariStartDate || cariEndDate) && (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold', padding: '10px' }}>
                              Filtrelenmiş Bakiye:
                            </td>
                            <td
                              className={`amount-cell ${(() => {
                                const filteredBalance = filteredTransactions.reduce((total, transaction) => {
                                  return total + (transaction.borc || 0) - (transaction.alacak || 0);
                                }, 0);
                                return filteredBalance >= 0 ? 'borc' : 'alacak';
                              })()}`}
                              style={{ fontWeight: 'bold', padding: '10px' }}
                            >
                              {(() => {
                                const filteredBalance = filteredTransactions.reduce((total, transaction) => {
                                  return total + (transaction.borc || 0) - (transaction.alacak || 0);
                                }, 0);
                                return formatCurrency(filteredBalance);
                              })()}
                            </td>
                            <td></td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold', padding: '10px' }}>
                            Toplam Bakiye:
                          </td>
                          <td
                            className={`amount-cell ${(() => {
                              const debtData = customerDebts[selectedCari.id];
                              const dbAmount = debtData && typeof debtData.amount === 'number' ? debtData.amount : 0;
                              const calculatedAmount = calculateBalance(filteredTransactions);
                              const finalAmount = calculatedAmount !== dbAmount ? calculatedAmount : dbAmount;
                              return finalAmount >= 0 ? 'borc' : 'alacak';
                            })()}`}
                            style={{ fontWeight: 'bold', padding: '10px' }}
                          >
                            {customerDebts[selectedCari.id]?.loading ? (
                              <span className="loading-balance">Yükleniyor...</span>
                            ) : (
                              (() => {
                                const debtData = customerDebts[selectedCari.id];
                                const dbAmount = debtData && typeof debtData.amount === 'number' ? debtData.amount : 0;
                                const calculatedAmount = calculateBalance(filteredTransactions);
                                const finalAmount = calculatedAmount !== dbAmount ? calculatedAmount : dbAmount;
                                return formatCurrency(finalAmount);
                              })()
                            )}
                          </td>
                          <td>
                            <div className="report-buttons">
                              <button 
                                className="excel-report-btn"
                                onClick={() => setKdvReportModal({ open: true, type: 'excel', cari: selectedCari })}
                                title="Excel Raporu İndir"
                              >
                                <Download size={16} />
                                Excel Raporu
                              </button>
                              <button 
                                className="excel-report-btn debt-only-btn"
                                onClick={() => setKdvReportModal({ open: true, type: 'hakedis', cari: selectedCari })}
                                title="Hakediş Raporu - Sadece Borçları Gösterir"
                                style={{ backgroundColor: '#e67e22' }}
                              >
                                <Download size={16} />
                                Hakediş Raporu
                              </button>
                              <button 
                                className="pdf-report-btn"
                                onClick={() => setKdvReportModal({ open: true, type: 'pdf', cari: selectedCari })}
                                title="PDF Raporu İndir"
                              >
                                <FileText size={16} />
                                PDF Raporu
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* İşlem Ekleme Modal */}
      {showAddTransactionModal && (
        <div className="modal-overlay" onClick={closeAddTransactionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Borç Ekle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    value={`Müşteri Adı: ${newTransaction.customerName}`}
                    className="form-input"
                    disabled
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    value={`Cari Kod: ${newTransaction.cariCode}`}
                    className="form-input"
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    value="İşlem Türü: Borç"
                    className="form-input"
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="number"
                    step="0.01"
                    value={newTransaction.amount}
                    onChange={(e) => handleTransactionInputChange('amount', e.target.value)}
                    className="form-input"
                    placeholder="Miktar: 0,00"
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="date"
                    value={newTransaction.date}
                    onChange={(e) => handleTransactionInputChange('date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <textarea
                    value={newTransaction.description}
                    onChange={(e) => handleTransactionInputChange('description', e.target.value)}
                    className="form-input"
                    placeholder="Açıklama (isteğe bağlı)"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={closeAddTransactionModal}
                type="button"
              >
                Vazgeç (ESC)
              </button>
              <button 
                className="btn-primary"
                onClick={handleAddTransaction}
                type="button"
                disabled={!newTransaction.date || !newTransaction.amount}
              >
                Borç Ekle (F4)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alacak Ekleme Modal */}
      {showAddCreditModal && (
        <div className="modal-overlay" onClick={closeAddCreditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Alacak Ekle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    value={`Müşteri Adı: ${newCredit.customerName}`}
                    className="form-input"
                    disabled
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    value={`Cari Kod: ${newCredit.cariCode}`}
                    className="form-input"
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <select
                    value={newCredit.paymentDescription}
                    onChange={(e) => handleCreditInputChange('paymentDescription', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Ödeme Şekli Seçin</option>
                    {paymentMethodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <input
                    type="number"
                    step="0.01"
                    value={newCredit.amount}
                    onChange={(e) => handleCreditInputChange('amount', e.target.value)}
                    className="form-input"
                    placeholder="Alacak Miktarı: 0,00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="date"
                    value={newCredit.date}
                    onChange={(e) => handleCreditInputChange('date', e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <textarea
                    value={newCredit.description}
                    onChange={(e) => handleCreditInputChange('description', e.target.value)}
                    className="form-input"
                    placeholder="Açıklama (isteğe bağlı)"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={closeAddCreditModal}
                type="button"
              >
                Vazgeç (ESC)
              </button>
              <button 
                className="btn-primary add-credit"
                onClick={handleAddCredit}
                type="button"
                disabled={!newCredit.date || !newCredit.paymentDescription || !newCredit.amount}
              >
                Alacak Ekle (F4)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteConfirmModal && transactionToDelete && (
        <div className="modal-overlay" onClick={closeDeleteConfirmModal}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>İşlem Silme Onayı</h3>
            </div>
            
            <div className="modal-body">
              <div className="confirm-content">
                <div className="warning-icon">⚠️</div>
                <div className="confirm-text">
                  <p><strong>Bu işlemi silmek istediğinizden emin misiniz?</strong></p>
                  <div className="transaction-details">
                    <p><strong>Tarih:</strong> {formatDate(transactionToDelete.date)}</p>
                    <p><strong>Açıklama:</strong> {transactionToDelete.description}</p>
                    <p><strong>Miktar:</strong> {formatCurrency(transactionToDelete.borc || transactionToDelete.alacak)}</p>
                    <p><strong>Tür:</strong> {transactionToDelete.borc > 0 ? 'Borç' : 'Alacak'}</p>
                  </div>
                  <p className="warning-text">
                    Bu işlem geri alınamaz ve müşterinin toplam bakiyesi yeniden hesaplanacaktır.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={closeDeleteConfirmModal}
                type="button"
              >
                Vazgeç
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteTransaction}
                type="button"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Başarı Popup (Silme) */}
      {showSuccessPopup && deletedTransactionInfo && (
        <div className="success-popup-overlay" onClick={closeSuccessPopup}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✅</div>
            <div className="success-title">İşlem Başarıyla Silindi!</div>
            <div className="success-message">
              Seçili işlem cari kasa geçmişinden kaldırıldı ve bakiye güncellendi.
            </div>
            <div className="success-details">
              <div><strong>Tarih:</strong> {formatDate(deletedTransactionInfo.date)}</div>
              <div><strong>Açıklama:</strong> {deletedTransactionInfo.description}</div>
              <div><strong>Miktar:</strong> {formatCurrency(deletedTransactionInfo.amount)}</div>
              <div><strong>Tür:</strong> {deletedTransactionInfo.type}</div>
              {deletedTransactionInfo.newBalance !== undefined && (
                <div><strong>Yeni Bakiye:</strong> {formatCurrency(deletedTransactionInfo.newBalance)}</div>
              )}
              {deletedTransactionInfo.hasHiddenRecords && (
                <div style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                  ⚠️ İlgili gizli transfer kayıtları da silindi
                </div>
              )}
            </div>
            <button className="success-close-btn" onClick={closeSuccessPopup}>
              Tamam
            </button>
          </div>
        </div>
      )}

      {/* Başarı Popup (Borç/Alacak Ekleme) */}
      {showAddSuccessPopup && addedTransactionInfo && (
        <div className="success-popup-overlay" onClick={closeAddSuccessPopup}>
          <div className="success-popup add-success" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">🎉</div>
            <div className="success-title">
              {addedTransactionInfo.type} Başarıyla Eklendi!
            </div>
            <div className="success-message">
              {addedTransactionInfo.type === 'Borç' ? 
                'Borç kaydı oluşturuldu ve bakiye güncellendi.' :
                'Alacak kaydı oluşturuldu ve bakiye güncellendi.'
              }
            </div>
            <div className="success-details">
              <div><strong>Müşteri:</strong> {addedTransactionInfo.customerName}</div>
              <div><strong>Tarih:</strong> {addedTransactionInfo.date}</div>
              <div><strong>Ödeme Şekli:</strong> {addedTransactionInfo.paymentMethod}</div>
              <div><strong>Miktar:</strong> {formatCurrency(addedTransactionInfo.amount)}</div>
              <div><strong>Açıklama:</strong> {addedTransactionInfo.description}</div>
            </div>
            <button className="success-close-btn" onClick={closeAddSuccessPopup}>
              Tamam
            </button>
          </div>
        </div>
      )}

      {kdvReportModal.open && kdvReportModal.cari && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kdv-report-modal-title"
          onClick={() => setKdvReportModal({ open: false, type: null, cari: null })}
        >
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="kdv-report-modal-title">Rapor — KDV</h3>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, lineHeight: 1.5, color: 'var(--text-color, #333)' }}>
                İşlem satırları her zaman <strong>KDV hariç</strong> gösterilir; <strong>TOPLAM</strong> satırı her iki seçenekte de aynıdır.
                <strong> KDV&apos;li</strong> seçerseniz yalnızca toplamın altına net bakiye üzerinden <strong>KDV (%20)</strong> ve <strong>genel toplam</strong> eklenir.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="excel-report-btn"
                  style={{ backgroundColor: '#95a5a6' }}
                  onClick={() => setKdvReportModal({ open: false, type: null, cari: null })}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="excel-report-btn"
                  onClick={() => {
                    const { type, cari } = kdvReportModal;
                    setKdvReportModal({ open: false, type: null, cari: null });
                    if (type === 'excel') generateExcelReport(cari, false);
                    else if (type === 'hakedis') generateExcelReportDebtOnly(cari, false);
                    else if (type === 'pdf') void generatePDFReport(cari, false);
                  }}
                >
                  KDV&apos;siz
                </button>
                <button
                  type="button"
                  className="pdf-report-btn"
                  onClick={() => {
                    const { type, cari } = kdvReportModal;
                    setKdvReportModal({ open: false, type: null, cari: null });
                    if (type === 'excel') generateExcelReport(cari, true);
                    else if (type === 'hakedis') generateExcelReportDebtOnly(cari, true);
                    else if (type === 'pdf') void generatePDFReport(cari, true);
                  }}
                >
                  KDV&apos;li (%20)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Düzenleme modalı */}
      <EditCariTransactionModal
        isOpen={showEditTransactionModal}
        onClose={closeEditTransactionModal}
        transaction={editingTransaction}
        onSave={handleEditTransaction}
        paymentMethodOptions={paymentMethodOptions}
      />
    </div>
  );
};

export default CariKasa; 